import type { DiffGranularity, IssueArea, TimeGap } from '@dispositio/core';

/** Einheiten eines Abstands in der Zeit: eins, mehrere */
const GAP_UNITS: Record<TimeGap['unit'], [string, string]> = { year: ['Jahr', 'Jahre'], month: ['Monat', 'Monate'], day: ['Tag', 'Tage'], hour: ['Stunde', 'Stunden'], minute: ['Minute', 'Minuten'] };
const gapAmount = (g: TimeGap): string => `${g.approx ? 'etwa ' : ''}${g.amount} ${GAP_UNITS[g.unit][g.amount === 1 ? 0 : 1]}`;

/** Titel des Fensters „Neu anlegen“ je vordefinierter Klasse */
const NEW_TITLE: Record<string, string> = { Person: 'Neue Person', Ort: 'Neuer Ort', Landschaft: 'Neue Landschaft', Gegenstand: 'Neuer Gegenstand', Organisation: 'Neue Organisation', Konzept: 'Neues Konzept' };

/** Alle sichtbaren Texte (Deutsch). */
export const de = {
  app: { name: 'Dispositio', tagline: 'Geschichten als Netz aus Personen, Orten, Ereignissen und Zeiträumen planen' },
  common: {
    loading: 'Lädt …',
    save: 'Speichern',
    saved: 'Gespeichert',
    cancel: 'Abbrechen',
    create: 'Anlegen',
    delete: 'Löschen',
    close: 'Schließen',
    retry: 'Erneut versuchen',
    none: '–',
    copy: 'Kopieren',
    add: 'Hinzufügen',
    remove: 'Entfernen',
    edit: 'Bearbeiten',
    ok: 'OK',
    dateTimePlaceholder: 'TT.MM.JJJJ hh:mm',
    calendarPlaceholder: (month: string) => `TT. ${month} JJJJ hh:mm`,
    calendarInvalid: (example: string) => `Bitte als Datum im Kalender der Geschichte eingeben, z. B. ${example} (auch TT.MM.JJJJ hh:mm) – oder als eigene Zählung, z. B. „Tag 12, 06:00“.`,
    pickDate: 'Datum im Kalender wählen',
    dateTimeInvalid: 'Bitte als TT.MM.JJJJ hh:mm eingeben (24 Stunden), z. B. 03.11.2030 18:30.',
    timeInvalid: 'Bitte als TT.MM.JJJJ hh:mm eingeben (24 Stunden), z. B. 03.11.2030 18:30 – oder als eigene Zählung, z. B. „Tag 12, 06:00“.',
    countPlaceholder: { minutes: 'Minute 45', hours: 'Stunde 5, Minute 30', days: 'Tag 12, 06:00', years: 'Jahr 340, Tag 120' } as Record<'minutes' | 'hours' | 'days' | 'years', string>,
    countInvalid: (example: string) => `Bitte als Zahl oder wie „${example}“ eingeben – oder als Datum (TT.MM.JJJJ hh:mm).`,
  },
  auth: {
    email: 'E-Mail',
    password: 'Passwort',
    passwordHint: 'Mindestens 10 Zeichen.',
    login: 'Anmelden',
    register: 'Registrieren',
    logout: 'Abmelden',
    noAccount: 'Noch kein Konto?',
    haveAccount: 'Schon registriert?',
    forgot: 'Passwort vergessen?',
    privacy: 'Deine Geschichten gehören nur dir. Es werden keine Daten an KI-Dienste gesendet.',
    resetTitle: 'Passwort zurücksetzen',
    resetIntro: 'Gib deine E-Mail-Adresse ein. Wir schicken dir einen Link zum Zurücksetzen.',
    resetSend: 'Link anfordern',
    resetNewPassword: 'Neues Passwort',
    resetSave: 'Passwort speichern',
    resetInvalid: 'Der Link ist ungültig oder abgelaufen. Bitte fordere einen neuen an.',
    resetSent: 'Wenn die Adresse bekannt ist, haben wir einen Link geschickt. Er ist eine Stunde gültig.',
    resetDone: 'Dein Passwort wurde geändert. Bitte melde dich neu an.',
    resetMissing: 'Dieser Link ist unvollständig. Bitte fordere einen neuen an.',
    backToLogin: 'Zur Anmeldung',
    loginTitle: 'Anmelden',
    loginRequired: 'Bitte melde dich an, um weiterzumachen.',
    registerTitle: 'Für die Beta registrieren',
    registerIntro: 'Leg dein Konto an – danach geht es gleich los.',
    passwordRepeat: 'Passwort wiederholen',
    passwordMismatch: 'Die beiden Passwörter stimmen nicht überein.',
    inviteCode: 'Einladungscode',
    inviteHint: 'Die Beta läuft auf Einladung. Den Code hast du mit deiner Einladung bekommen.',
    closed: 'Die Registrierung für die Beta ist gerade geschlossen. Wer schon ein Konto hat, meldet sich wie gewohnt an.',
    betaNote: 'Beta: Eine Bestätigung per E-Mail gibt es noch nicht.',
    noReset: 'Beta: Eine Bestätigung per E-Mail und das Zurücksetzen des Passworts gibt es noch nicht – merk dir dein Passwort gut.',
    devLogin: 'Entwicklung: als Demo-Konto weiter',
    toLanding: 'Zur Startseite',
  },
  footer: {
    label: 'Weitere Seiten',
    about: 'Über',
    imprint: 'Impressum',
    privacy: 'Datenschutz',
  },
  info: {
    aboutTitle: 'Über Dispositio',
    imprintTitle: 'Impressum',
    privacyTitle: 'Datenschutz\u00ADerklärung',
    version: (v: string) => `Version ${v}`,
  },
  landing: {
    beta: 'Beta',
    nav: 'Anmeldung',
    login: 'Anmelden',
    register: 'Registrieren',
    toApp: 'Zu deinen Geschichten',
    eyebrow: 'Für alle, die Geschichten erzählen',
    title: 'Ordne den Stoff deiner Geschichte.',
    lead: 'Dispositio verbindet Figuren und Welt, die Handlung auf Zeitlinien und das Manuskript in Kapiteln. Was zusammengehört, bleibt verknüpft – vom ersten Einfall bis zum letzten Satz.',
    ctaOpen: 'Für die Beta registrieren',
    ctaInvite: 'Mit Einladungscode registrieren',
    haveAccount: 'Ich habe schon ein Konto',
    inviteNote: 'Die Beta läuft auf Einladung: Für die Registrierung brauchst du einen Einladungscode.',
    closed: 'Die Registrierung für die Beta ist gerade geschlossen. Wer schon ein Konto hat, meldet sich wie gewohnt an.',
    betaTitle: 'Jetzt in der Beta',
    betaText: 'Dispositio wächst gerade. Funktionen können sich noch ändern – und deine Rückmeldung fließt direkt in die Entwicklung ein.',
    /** Bedeutung des Namens: kleiner Lexikoneintrag zwischen dem oberen Teil und „Jetzt in der Beta“ */
    term: {
      label: 'Bedeutung des Namens',
      word: 'Dispositio',
      origin: 'dispositio, lat.:',
      meanings: ['geordnete Anordnung von Aktivitäten', 'geordnete Anordnung von Worten', 'geordnete Anordnung von Zeit', 'systematische Anordnung von Argumenten'],
    },
    visual: {
      tabs: ['Bibliothek', 'Handlung', 'Manuskript'],
      start: 'Tag 1',
      end: 'Tag 9',
      scenes: ['Die Ankunft', 'Der Sturm', 'Flucht', 'Abschied'],
      chapters: ['1 · Ankunft', '2 · Sturm', '3 · Nachspiel'],
      person: 'Anna Berg',
      role: 'Leuchtturmwärterin',
      chapter: 'Kapitel 2',
      text: ['Der Wind drückte gegen die Tür, als ', 'Anna', ' die Lampe entzündete.'],
    },
  },
  /** Entitäten einer Szene (Tab „Entitäten“) und Entitätenbrowser */
  objects: {
    add: 'Entitäten hinzufügen',
    empty: 'Noch keine Entitäten in dieser Szene.',
    emptyHint: 'Füge sie mit „+ Entitäten hinzufügen“ hinzu, zieh auf der Fläche eine Karte auf die Szene oder verknüpfe sie in einem Text mit @.',
    noCategory: 'Ohne Kategorie',
    open: 'Entität öffnen',
    browserTitle: 'Entitäten hinzufügen',
    search: 'Entitäten suchen …',
    areas: 'Bereiche',
    all: 'Alle',
    none: 'Nichts gefunden.',
    libraryEmpty: 'Die Bibliothek ist noch leer. Lege in der Bibliothek Personen, Orte, Gegenstände, Organisationen oder Konzepte an.',
    here: 'in der Szene',
    addOne: 'hinzufügen',
    addLabel: (title: string) => `„${title}“ zur Szene hinzufügen`,
    hereLabel: (title: string) => `„${title}“ ist schon in der Szene`,
    done: 'Fertig',
  },
  projects: {
    title: 'Meine Geschichten',
    new: 'Neue Geschichte',
    name: 'Titel der Geschichte',
    nodes: (n: number) => (n === 1 ? '1 Eintrag' : `${n.toLocaleString('de-DE')} Einträge`),
    deleteConfirm: (name: string) => `„${name}“ mit allem Inhalt unwiderruflich löschen?`,
    linked: (f: string) => `Ordner: ${f}`,
    count: (n: number) => (n === 1 ? '1 Geschichte' : `${n.toLocaleString('de-DE')} Geschichten`),
    edited: (when: string) => `bearbeitet ${when}`,
    deleteLabel: (name: string) => `„${name}“ löschen`,
    settingsLabel: (name: string) => `Einstellungen von „${name}“`,
    backupLabel: (name: string) => `Sicherung von „${name}“ herunterladen (ZIP mit Texten, Bildern und Versionen)`,
    restore: 'Sicherung einlesen …',
    restoring: 'Liest ein …',
    restoreHint: 'Eine Projekt-Sicherung (ZIP) als neue Geschichte einlesen – die vorhandenen bleiben, wie sie sind',
    restored: (name: string) => `„${name}“ aus der Sicherung angelegt`,
    open: 'Öffnen',
    settingsTitle: (name: string) => `Einstellungen · ${name}`,
    newCard: 'Neue Geschichte anlegen',
    nav: 'Konto',
    /** Gruppen der Übersicht: oben die Tutorials, darunter die Serien des Kontos */
    tutorials: 'Tutorials',
    tutorialStep: (step: number, of: number) => `Schritt ${step} von ${of} · weitermachen`,
    tutorialOpenHint: (title: string) => `„${title}“ öffnen und dort weitermachen, wo du warst`,
    tutorialStartHint: (title: string) => `„${title}“ beginnen`,
    tutorialRestart: 'Neu beginnen',
    tutorialRestartHint: 'Das Tutorial frisch ab dem ersten Schritt beginnen; der bisherige Stand darin wird ersetzt.',
    tutorialRestartConfirm: (title: string) => `„${title}“ neu beginnen? Dein bisheriger Stand darin wird durch ein frisches Tutorial ersetzt.`,
    expand: (name: string) => `${name} aufklappen`,
    collapse: (name: string) => `${name} zuklappen`,
    seriesHint: 'Doppelklick benennt um · Geschichten lassen sich hierher ziehen',
    episode: (n: number) => `Episode ${n}`,
    episodeHint: 'Die Geschichten einer Serie sind ihre Episoden. Zum Umsortieren ein Cover auf ein anderes ziehen.',
    seriesName: 'Name der Serie',
    seriesRename: 'Umbenennen',
    seriesDelete: 'Serie löschen',
    seriesLast: 'Die letzte Serie bleibt',
    seriesDeleteConfirm: (name: string, n: number) =>
      `Die Serie „${name}“ enthält ${n === 1 ? 'eine Geschichte' : `${n} Geschichten`}. Die Serie samt ${n === 1 ? 'dieser Geschichte' : `allen ${n} Geschichten`} unwiderruflich löschen?`,
    newSeries: '+ Neue Serie',
    newSeriesDefault: 'Neue Serie',
    newIn: (series: string) => `In der Serie „${series}“`,
  },
  // Fenster beim Anlegen einer Episode (PLAN.md §43): Elemente früherer Episoden als Referenzen übernehmen
  episodeRefs: {
    title: 'Aus früheren Episoden übernehmen',
    intro: (series: string, name: string) =>
      `Diese Elemente stehen in früheren Episoden von „${series}“. Gewählte kommen als Referenzen in „${name}“: mit demselben Steckbrief und ihrem Stand am Ende der früheren Episoden. Was in der neuen Episode keine Rolle spielt, wähle ab.`,
    fromEpisode: (n: number, name: string) => `Original in Episode ${n} („${name}“)`,
    all: 'Alle',
    clear: 'Keine',
    back: '← Zurück',
    none: 'Ohne Referenzen anlegen',
    create: (n: number) => (n === 0 ? 'Anlegen' : n === 1 ? 'Mit 1 Referenz anlegen' : `Mit ${n} Referenzen anlegen`),
  },
  // Serien (PLAN.md §43): Elemente und Vorgeschichte aus früheren Episoden
  series: {
    reference: (n: number) => `Referenz aus Episode ${n}`,
    referenceHint: (n: number, name: string) => `Referenz: Das Original steht in Episode ${n} („${name}“). Der Steckbrief gilt in allen Episoden – eine Änderung hier gilt auch dort.`,
    original: (episodes: number[]) => `auch in Episode ${episodes.join(', ')}`,
    originalHint: (episodes: number[]) => `Das Original – als Referenz auch in Episode ${episodes.join(', ')}. Der Steckbrief gilt überall.`,
    sharedNote: (episodes: number[]) => `Der Steckbrief gilt in allen Episoden, in denen es steht (${episodes.join(', ')}).`,
    prior: (n: number) => `Stand am Ende von Episode ${n}`,
    priorHint: 'Was die früheren Episoden über das Element festhalten: Ausgangslage dieser Episode. Ändern lässt es sich in einer Szene dieser Episode.',
    holds: (item: string) => `hat ${item}`,
    inherited: (n: number) => `aus Ep. ${n}`,
    inheritedHint: (n: number) =>
      `Gilt seit Episode ${n}. Ändern (Haltung, Wendepunkt) übernimmt die Beziehung in diese Episode; enden lassen: „… bis <Szene>“ in der Eingabezeile.`,
    removeReference: 'Aus dieser Episode entfernen',
    removeReferenceConfirm: (title: string, n: number, name: string) =>
      `„${title}“ nur aus dieser Episode entfernen? Es ist eine Referenz – vollständig löschen lässt es sich nur in Episode ${n} („${name}“), wo es entstand. Hier verschwindet es samt seinen Verweisen.`,
    deleteEverywhere: (episodes: number[]) => ` Es verschwindet auch aus Episode ${episodes.join(', ')}, wo es als Referenz steht.`,
    episodeOf: (n: number, count: number, series: string) => `Episode ${n} von ${count} · ${series}`,
  },
  profile: {
    title: 'Profil',
    photo: 'Profilfoto',
    photoChoose: 'Foto wählen …',
    photoChange: 'Anderes Foto …',
    photoRemove: 'Foto entfernen',
    photoUploading: 'Lädt hoch …',
    photoHint: 'JPEG, PNG oder WebP. Das Foto wird mittig quadratisch zugeschnitten und erscheint oben rechts in der Kopfleiste.',
    photoSaved: 'Profilfoto gespeichert',
    photoRemoved: 'Profilfoto entfernt',
    back: 'Zurück zur Geschichte',
    dangerTitle: 'Konto löschen',
    dangerHelp: 'Entfernt dein Konto und alle Geschichten unwiderruflich.',
    confirmPassword: 'Zur Bestätigung dein Passwort',
    deleteAccount: 'Konto endgültig löschen',
  },
  dock: {
    docked: 'An der Zeitlinie angedockt',
    undocked: 'Von der Zeitlinie gelöst',
    undock: 'Von der Zeitlinie lösen',
    dock: 'Andocken',
    position: 'Position auf der Zeitlinie',
    status: (timeline: string) => `Angedockt an „${timeline}“`,
    loose: 'Lose (nicht an der Zeitlinie)',
    derived: 'Die Zeit legt die Stelle auf der Zeitlinie fest. Zieh die Szene dorthin, wo sie stattfinden soll, oder gib die Zeit an.',
    looseHint: 'Gib oben eine Zeit an: Die Szene dockt dann von selbst an ihrer Stelle an der Zeitlinie an. Oder zieh sie auf eine Zeitlinie.',
    offCanvas: 'Nicht auf der Fläche',
    offCanvasHint: 'Die Szene steht nur im Manuskript. Gibst du oben eine Zeit an, dockt sie von selbst an ihrer Stelle an der Zeitlinie an. Sonst docke sie hier an, lege sie lose auf die Fläche oder zieh ihre Karte aus der Erzähllinie auf die Zeichenfläche.',
    takeOff: 'Von der Fläche nehmen',
    takeOffHint: 'Die Szene bleibt im Manuskript (mit ihrem Text und Kapitel); ihre Zeit bleibt.',
    takenOff: 'Von der Fläche genommen – die Szene bleibt im Manuskript',
    putOnLabel: 'Lose auf die Fläche legen',
    putOn: 'Auf die Fläche gelegt',
    dockTo: (title: string) => `An „${title}“ andocken`,
    putInto: (title: string) => `In „${title}“ ablegen`,
    moved: (title: string) => `Verschoben nach „${title}“`,
    hint: 'Weit von der Zeitlinie weggezogen löst sich die Szene wieder.',
    noDates: 'Die Zeitlinie hat noch keine Zeitrechnung (Datum oder eigene Zählung bei Start und Ende); die Zeit ist nur eine Position.',
    needsDates: 'Lege an der Zeitlinie Start und Ende fest (Datum und Uhrzeit oder eine eigene Zählung), dann wählst du hier direkt die Zeit. Bis dahin verschiebst du die Szene auf der Zeitlinie.',
    needsDatesShort: 'Die Zeitlinie hat noch keine Zeitrechnung.',
    derivedDates: 'Die Zeit legt die Stelle auf der Zeitlinie fest – und umgekehrt: Ziehst du die Szene oder ihre Ränder, ändert sich die Zeit.',
    badDate: 'Diese Zeit lässt sich nicht auf die Zeitlinie übertragen (ein Datum braucht bei einer eigenen Zählung einen Nullpunkt).',
    endAfterStart: 'Das Ende muss nach dem Beginn liegen.',
  },
  /** Zeit einer Szene (PLAN.md §15): Zeitpunkt bzw. Beginn und Ende, Genauigkeit, ungefähr, Herkunft */
  sceneTime: {
    weekday: (name: string) => `Wochentag: ${name}`,
    // erschlossene Zeit (PF 52)
    inferred: (after: string, before: string, prev: string, next: string) => `Erschlossen: zwischen ${after} (${prev}) und ${before} (${next}) – aus den Nachbarn in Lesereihenfolge, nicht gespeichert.`,
    inferredAdopt: 'Als Zeit übernehmen',
    inferredAdoptHint: 'Setzt den Beginn auf das Ende der Szene davor, als „ungefähr“ – danach lässt sich die Zeit genauer setzen',
    inferredBlur: (title: string) => `„${title}“: erschlossene Zeit (aus den Nachbarn in Lesereihenfolge)`,
    title: 'Zeit',
    precision: 'Genauigkeit',
    precisions: { minute: 'Minute', hour: 'Stunde', day: 'Tag', month: 'Monat', year: 'Jahr' } as Record<string, string>,
    precisionHint: 'Wie genau die Zeit bekannt ist – angezeigt wird nur bis dahin (z. B. „03.1823“ statt „03.03.1823 00:00“).',
    approx: 'ungefähr',
    approxHint: 'Die Zeit ist geschätzt („gegen acht“, „im Frühling“) und wird mit „ca.“ angezeigt.',
    mismatch: (time: string) => `Die Zeitmarken im Text ergeben ${time}. Die Szene wurde seitdem verschoben oder von Hand gesetzt.`,
    mismatchAdopt: 'Zeit aus dem Text übernehmen',
    mismatchHint: 'Setzt die Szene auf die Zeit, die ihre Zeitmarken im Szenentext ergeben; danach folgt sie wieder dem Text.',
    mismatchShow: 'Im Text zeigen',
    mismatchShowHint: 'Öffnet den Szenentext: Die Zeitmarken, die nicht mehr passen, sind rot – mit einem Vorschlag für den Text.',
    source: (from: string) => `Herkunft: ${({ text: 'aus dem Text', timeline: 'von der Zeitlinie', manual: 'von Hand' } as Record<string, string>)[from] ?? from}`,
    clear: 'Zeit entfernen',
    none: 'Noch keine Zeit.',
    // Ende oder Dauer (PLAN.md §17)
    endMode: 'Ende',
    endModeHint: 'Ohne Ende ist die Szene ein Zeitpunkt; mit einem Ende oder einer Dauer ein Zeitraum.',
    endModes: { none: 'Kein Ende', end: 'Ende', duration: 'Dauer' } as Record<'none' | 'end' | 'duration', string>,
    duration: 'Dauer',
    until: (end: string) => `bis ${end}`,
    // relative Zeit (PLAN.md §16)
    relative: 'Relativ zu',
    relativeHint: 'Die Zeit hängt an einer anderen Szene: Verschiebt sie sich, zieht diese Szene nach.',
    noAnchor: '– keine (absolute Zeit) –',
    anchorText: (title: string) => `Relative Zeitangaben im Text („drei Tage später“) gehen von „${title}“ aus.`,
    offset: 'Abstand',
    offsetUnits: { min: 'Minuten', h: 'Stunden', d: 'Tage', w: 'Wochen', mo: 'Monate', y: 'Jahre' } as Record<string, string>,
    after: 'danach',
    before: 'davor',
    anchorScene: (title: string) => `Szene „${title}“`,
    untitled: 'Ohne Titel',
  },
  /** Zeitmarken im Text (PLAN.md §16) */
  timeMarks: {
    button: 'Zeit',
    buttonHint: 'Markierte Zeitangabe als Zeitmarke setzen bzw. ändern (Strg+⇧+6) – die Zeit der Szene folgt dem Text',
    title: (text: string) => `Zeitmarke: „${text}“`,
    input: 'Zeit',
    inputHint: 'Datum („03.03.1823 07:00“), relativ („+3d“, „+1d@07:00“) oder in Worten („drei Tage später“)',
    notRecognized: 'Keine Zeit erkannt.',
    // nicht erkannt (PLAN.md §16): was zu tun ist und in welchen Formaten
    unrecognized: (text: string) => `„${text}“ wird nicht automatisch als Zeit erkannt. Gib unten selbst an, welche Zeit gemeint ist – dein Text bleibt, wie er ist, und wird mit dieser Zeit verknüpft.`,
    notUnderstood: (input: string) => `„${input}“ ist keine Zeitangabe, die Dispositio versteht.`,
    inputPlaceholder: 'z. B. 01.01.2025',
    formatsTitle: 'So gibst du die Zeit an (anklicken übernimmt das Beispiel):',
    formats: [
      { label: 'Datum', examples: ['01.01.2025', '01.01.2025 07:00'] },
      { label: 'Tag ohne Jahr – das Jahr kommt aus der Szene davor', examples: ['1. Januar', '01.01.'] },
      { label: 'Relativ zur Szene davor bzw. zur Zeitmarke davor', examples: ['drei Tage später', '+3d', '+1d@07:00'] },
      { label: 'Eigene Zählung (Zeitlinie in Tagen oder Jahren)', examples: ['tag1', 'jahr340'] },
      { label: 'Dauer der Szene', examples: ['drei Tage lang', '3d'] },
    ] as { label: string; examples: string[] }[],
    // was die Zeitmarke für die Szene ergibt (Vorschau)
    sceneGets: (time: string) => `Zeit der Szene: ${time}`,
    sceneGetsFrom: (time: string, anchor: string) => `Zeit der Szene: ${time} (gerechnet ab „${anchor}“)`,
    noSceneTime: {
      yearless: 'Ohne Jahr: Vor dieser Szene gibt es keine Szene mit Datum, aus der das Jahr kommen könnte – so bekommt die Szene keine Zeit. Gib das Jahr mit an, z. B. „01.01.2025“.',
      relative: 'Relativ: Vor dieser Szene gibt es nichts mit Zeit, wovon aus gerechnet werden könnte – so bekommt die Szene keine Zeit. Setze einen festen Zeitpunkt, z. B. „01.01.2025“.',
      basis: 'Passt nicht zur Zeit davor (Datum und eigene Zählung lassen sich nicht verrechnen) – so bekommt die Szene keine Zeit. Nimm dieselbe Art wie davor, z. B. „tag1“ oder „01.01.2025“.',
    } as Record<'yearless' | 'relative' | 'basis', string>,
    notDraft: 'Zeitmarken bestimmen nur im Szenentext einer Szene ihre Zeit – hier ist es eine Notiz am Text.',
    // verknüpfter Text gelöscht: die Zeitmarke ist weg (geändert bleibt sie, PLAN.md §16d)
    lostDeleted: (text: string) => `Die Zeitangabe „${text}“ ist gelöscht – und mit ihr die Zeitmarke. Markiere die neue Zeitangabe und setze mit „Zeit“ eine neue, damit die Szene weiter dem Text folgt.`,
    setNew: 'Neue Zeit setzen',
    means: (text: string) => `Bedeutet: ${text}`,
    precisionAuto: 'wie angegeben',
    apply: 'Setzen',
    remove: 'Zeitmarke entfernen',
    pickFirst: 'Markiere zuerst die Zeitangabe im Text – oder setze den Cursor in eine Zeitmarke.',
    applied: (text: string) => `„${text}“ ist jetzt eine Zeitmarke.`,
    removed: 'Zeitmarke entfernt (der Text bleibt).',
    suggestTitle: (meaning: string) => `Mögliche Zeitangabe: ${meaning}`,
    accept: 'Als Zeitmarke setzen',
    ignore: 'Hier keine Zeitmarke',
    sameDay: 'am selben Tag',
    nextDay: 'am Tag danach',
    dayBefore: 'am Tag davor',
    after: 'danach',
    before: 'davor',
    yearFromContext: '(Jahr aus dem Zusammenhang)',
    units: { min: ['Minute', 'Minuten'], h: ['Stunde', 'Stunden'], d: ['Tag', 'Tage'], w: ['Woche', 'Wochen'], mo: ['Monat', 'Monate'], y: ['Jahr', 'Jahre'] } as Record<string, [string, string]>,
    // Rolle einer Zeitmarke (PLAN.md §17)
    role: 'Rolle',
    roleHint: 'Was die Zeitmarke für die Szene bedeutet: ihr Beginn, ihr Ende oder ihre Dauer. „Von selbst“: die erste Zeitmarke ist der Beginn, weitere nur Zeitangaben im Verlauf.',
    roleAuto: 'von selbst',
    roles: { start: 'Beginn', end: 'Ende', duration: 'Dauer' } as Record<'start' | 'end' | 'duration', string>,
  },
  // Wissen beim Schreiben (PLAN.md §16b)
  knowledge: {
    title: 'Wissen',
    knows: 'Weiß',
    knownBy: 'Wer es weiß',
    reader: 'Der Leser',
    readerNot: 'Der Leser: noch nicht enthüllt',
    since: (scene: string) => `seit ${scene}`,
    again: (n: number) => (n === 1 ? 'noch 1-mal erfahren' : `noch ${n}-mal erfahren`),
  },
  // Zustand über die Zeit und Beziehungen (PLAN.md §16c)
  states: {
    title: 'Beziehungen und Zustand',
    relations: 'Beziehungen',
    history: 'Zustand über die Zeit',
    noRelations: 'Keine Beziehungen.',
    noHistory: 'Keine Änderungen.',
    historyHint: 'Änderungen entstehen in Szenen – aus dem Text oder über die Eingabezeile unten.',
    sceneTitle: 'Änderungen in dieser Szene',
    sceneHint: 'Was sich hier ändert: Verfassung (geboren, gesund, verletzt, tot), Aufenthaltsort, Besitz, Merkmale („Haare: grau“). Der Szenentext schlägt Änderungen vor („stirbt“, „reist nach …“, „nimmt … an sich“), ebenso Titel und Kurzbeschreibung („Maras Geburt“, „Tod des Vaters“); ✓ übernimmt sie hierher.',
    sceneNone: 'Nichts festgehalten.',
    since: (scene: string) => `ab ${scene}`,
    until: (scene: string) => `bis ${scene}`,
    remove: 'Entfernen',
    fromText: (quote: string) => `aus dem Text: „${quote}“`,
    manual: 'von Hand',
    gets: (item: string) => `bekommt ${item}`,
    has: (item: string) => `hat ${item}`,
    condition: { geboren: 'geboren', gesund: 'gesund', verletzt: 'verletzt', tot: 'tot' } as Record<string, string>,
    // Entf in einer Zeile (die Knöpfe ✕ löschen ohne Rückfrage)
    removeRelationConfirm: (what: string) => `Die Beziehung „${what}“ entfernen?`,
    removeChangeConfirm: (what: string) => `Die Änderung „${what}“ entfernen?`,
    removeTurnConfirm: 'Diesen Wendepunkt entfernen?',
    at: (place: string) => `in ${place}`,
    with: (holder: string) => `bei ${holder}`,
    lost: 'verloren',
    from: (what: string, giver: string) => `${what} (von ${giver})`,
    // Merkmale (AP-31)
    trait: (trait: string, value: string) => `${trait}: ${value}`,
    traitGone: (trait: string) => `${trait} fällt weg`,
    fromStart: 'von Anfang an (Feld „Besitzer“)',
    // Haltung und Wendepunkte (AP-30)
    attitude: { zugetan: 'zugetan', neutral: 'neutral', abgeneigt: 'abgeneigt' } as Record<string, string>,
    attitudeUnset: 'Haltung –',
    ownAttitude: (name: string) => `Haltung zu ${name}`,
    otherAttitude: (name: string) => `Haltung von ${name}`,
    turns: 'Wendepunkte',
    addTurn: '+ Wendepunkt',
    addTurnHint: 'Ab einer Szene ändert sich die Haltung (in Weltzeit): Szene wählen, Haltung je Richtung, kurze Notiz',
    turnScene: 'Ab Szene',
    turnNote: 'Notiz (z. B. der Verrat)',
    turnLine: (scene: string) => `ab ${scene}`,
    turnSave: 'Hinzufügen',
    attitudeShort: (own: string | null, other: string | null) => [own ? `selbst ${own}` : '', other ? `erwidert ${other}` : ''].filter(Boolean).join(' · '),
    // Berechnete Verwandtschaft (AP-30)
    kinship: 'Verwandtschaft (berechnet)',
    kinshipHint: 'Ergibt sich aus Eltern, Geschwistern und Ehen – Großeltern, Onkel und Tanten, Cousinen, Schwiegereltern, Schwager.',
    via: (names: string) => `über ${names}`,
    // Reiseweg zwischen Orten (AP-40)
    travel: (minutes: string) => `Reisezeit ${minutes}`,
    // Eingabezeile in natürlicher Sprache (PF 7)
    inputLabel: 'Neu (in eigenen Worten)',
    entityPlaceholder: 'z. B. Mutter von Lisa · stirbt in Die Schlacht · verfeindet mit Ben ab Der Verrat · Haare: grau in 3.2',
    placePlaceholder: 'z. B. nach Linz: 3 Stunden (Reiseweg) · Mitglied von …',
    scenePlaceholder: 'z. B. Clara stirbt · Maras Geburt · Ben reist nach Rom · Clara gibt den Ring an Ben · Clara Haare: grau',
    apply: 'Übernehmen',
    applyHint: 'Enter übernimmt; fehlende Entitäten werden angelegt.',
    // Vorschläge im Draft (PF 6)
    hintTitle: (who: string, what: string) => `Mögliche Änderung: ${who} – ${what}. ✓ hält sie in der Szene fest, ✕: keine Änderung.`,
    accept: 'Als Änderung der Szene festhalten',
    ignore: 'Keine Änderung',
    done: (who: string, what: string) => `In der Szene festgehalten: ${who} – ${what}`,
    setTitle: (who: string, what: string) => `In der Szene festgehalten: ${who} – ${what}`,
    // Kontext einer Entität am verknüpften Wort
    glance: 'Stand hier',
    here: 'hier',
    // Vorschläge aus Titel und Kurzbeschreibung (PF 15)
    suggested: 'Vorschlag aus dem Text',
    suggestedHint: (text: string) => `Aus „${text}“ – ✓ hält es in der Szene fest.`,
    // Lebensdaten und Alter (PF 15)
    life: 'Lebensdaten',
    born: (when: string) => `geboren ${when}`,
    died: (when: string) => `gestorben ${when}`,
    diedAt: (age: string) => `mit ${age}`,
    age: 'Alter',
    unborn: 'noch nicht geboren',
    dead: 'tot',
    newborn: 'neugeboren',
    years: (n: number) => (n === 1 ? '1 Jahr' : `${n} Jahre`),
    yearsDat: (n: number) => (n === 1 ? '1 Jahr' : `${n} Jahren`),
    months: (n: number) => (n === 1 ? '1 Monat' : `${n} Monate`),
    monthsDat: (n: number) => (n === 1 ? '1 Monat' : `${n} Monaten`),
    days: (n: number) => (n === 1 ? '1 Tag' : `${n} Tage`),
    daysDat: (n: number) => (n === 1 ? '1 Tag' : `${n} Tagen`),
    about: (age: string) => `etwa ${age}`,
    noAge: 'Alter offen: die Zeiten lassen sich nicht vergleichen',
  },
  // Ort der Szene aus dem Text (PLAN.md §16a)
  places: {
    shiftTitle: (from: string, to: string) => `Möglicher Ortswechsel: ${from} → ${to}. ✓ macht „${to}“ zum Schauplatz der Szene, ✕: kein Ortswechsel.`,
    accept: 'Als Schauplatz der Szene setzen',
    ignore: 'Kein Ortswechsel',
    made: (name: string) => `„${name}“ ist jetzt Schauplatz der Szene.`,
    fromText: (name: string) => `Schauplatz der Szene aus dem Text: „${name}“.`,
  },
  views: {
    label: 'Modus',
    design: 'Bibliothek',
    planning: 'Handlung',
    editor: 'Manuskript',
  },
  design: {
    focusLabel: 'Bereiche',
    /** „Neue Person“, „Neuer Ort“, … (eigene Klassen: „Neu: Raumschiff“) */
    newTitle: (typeName: string) => NEW_TITLE[typeName] ?? `Neu: ${typeName}`,
    editTitle: (typeName: string) => `${typeName} bearbeiten`,
    search: 'Suchen …',
    display: 'Darstellung',
    grid: 'Karten',
    board: 'Pinnwand',
    boardHint: 'Frei anordnen; Kategorien sind hier Rahmen · Doppelklick: neue Entität an dieser Stelle',
    boardEmpty: 'Noch leer. Doppelklicke auf die Pinnwand, um hier eine Entität anzulegen.',
    cardHint: 'Öffnen und bearbeiten',
    empty: (plural: string) => `Noch keine ${plural}. Für den Anfang genügt ein Name – alles Weitere ergänzt du, wann immer du willst.`,
    noHits: 'Nichts gefunden.',
    scenes: (n: number) => (n === 1 ? 'in 1 Szene' : `in ${n} Szenen`),
    unused: 'noch nirgends',
    unusedLong: 'Kommt noch in keiner Szene vor.',
    title: 'Name',
    titlePlaceholder: 'Name eingeben …',
    imageHint: 'Bild hierher ziehen oder klicken',
    imageChange: 'Bild ändern',
    addField: 'Feld',
    addFieldTitle: 'Feld hinzufügen',
    addFieldHint: 'Ein neues Feld gilt ab sofort für alle Entitäten dieses Bereichs im Projekt.',
    suggestions: 'Vorschläge',
    ownField: 'Eigenes Feld',
    fieldName: 'Name des Feldes',
    fieldType: 'Art',
    fieldTypes: { text: 'Text', longtext: 'Langer Text', number: 'Zahl', date: 'Datum', image: 'Bild', choice: 'Auswahl' } as Record<string, string>,
    fieldOptions: 'Auswahl, durch Komma getrennt',
    fieldAdd: 'Hinzufügen',
    fieldDone: 'Fertig',
    fieldMenu: (label: string) => `Feld „${label}“`,
    fieldRename: 'Umbenennen',
    fieldRemove: 'Entfernen',
    fieldRemoveConfirm: (label: string, plural: string | null) =>
      plural ? `Das Feld „${label}“ bei allen ${plural} entfernen? Bereits eingetragene Werte werden ausgeblendet.` : `Das Feld „${label}“ entfernen? Ein eingetragener Wert wird ausgeblendet.`,
    fieldAdded: (label: string) => `„${label}“ ist jetzt Teil jeder Entität dieses Bereichs.`,
    create: 'Anlegen',
    createAndNext: 'Anlegen und weitere',
    createAndNextHint: 'Anlegen und gleich die nächste Entität beginnen',
    createHint: 'Name genügt – alles Weitere kannst du jederzeit ergänzen.',
    created: (name: string) => `„${name}“ angelegt.`,
    done: 'Fertig',
    delete: 'Löschen',
    advanced: 'Verweise und Vorkommen',
    world: 'Welt',
    worldIntro: 'Was für die ganze Geschichte gilt: die Welt, in der sie spielt. Sie fließt später in jede Szene ein.',
    premise: 'Prämisse',
    toolbar: 'Einträge und Kategorien',
    category: 'Kategorie',
    noCategory: 'Ohne Kategorie',
    newCategory: 'Kategorie',
    newCategoryHint: 'Neue Kategorie: in den Karten ein eigener Abschnitt, auf der Pinnwand ein Rahmen',
    categoryName: 'Name der Kategorie',
    categoryDefault: 'Neue Kategorie',
    categoryEmpty: 'Noch leer – zieh Karten hierher.',
    categoryHint: 'Karten hierher ziehen, um sie in diese Kategorie zu legen · Kopfzeile ziehen oder Alt+↑/↓: Reihenfolge ändern',
    categoryGrip: 'Ziehen, um die Reihenfolge der Kategorien zu ändern (oder Alt+↑/↓ auf dem Namen)',
    restHint: 'Hierher ziehen: aus der Kategorie nehmen',
    newIn: (what: string, category: string) => `${what} in „${category}“`,
    categoryRename: 'Umbenennen',
    categoryColor: 'Farbe',
    categoryDelete: 'Kategorie löschen',
    categoryDeleteConfirm: (name: string, n: number) =>
      n > 0 ? `Die Kategorie „${name}“ löschen? Die ${n === 1 ? 'Karte darin bleibt' : `${n} Karten darin bleiben`} erhalten (ohne Kategorie).` : `Die Kategorie „${name}“ löschen?`,
    collapse: (name: string) => `„${name}“ zuklappen`,
    expand: (name: string) => `„${name}“ aufklappen`,
    cardSize: 'Kartengröße',
    smaller: 'Kleinere Karten',
    bigger: 'Größere Karten',
  },
  /** Verlauf: Rückgängig und Wiederholen von Änderungen an der Geschichte */
  history: {
    verbs: { create: 'Anlegen', change: 'Ändern', move: 'Verschieben', remove: 'Löschen', convert: 'Umwandeln', text: 'Text bearbeiten', connect: 'Verbinden' },
    label: (verb: string, name: string) => (name ? `${verb}: „${name}“` : verb),
    connection: 'Verbindung',
    undo: 'Rückgängig',
    redo: 'Wiederholen',
    undoTitle: (label: string | null) => (label ? `Rückgängig: ${label} (Strg+Z)` : 'Nichts rückgängig zu machen'),
    redoTitle: (label: string | null) => (label ? `Wiederholen: ${label} (Strg+Y)` : 'Nichts zu wiederholen'),
    undone: (label: string) => `Rückgängig gemacht – ${label}`,
    redone: (label: string) => `Wiederholt – ${label}`,
  },
  toolbar: {
    projects: 'Zu den Geschichten',
    fit: 'Alles zeigen',
    fitHint: 'Alles zeigen – auf der Handlungsfläche springt ← bzw. → zur vorigen bzw. nächsten Szene (Weltzeit), Pos1/Ende zur ersten bzw. letzten',
    sidebar: 'Seitenleiste',
  },
  graph: {
    zoom: ['Fern', 'Mittel', 'Nah'],
    story: 'Geschichte',
  },
  library: {
    groupShort: 'Kategorie',
    newGroup: 'Neue Kategorie',
    groupName: 'Name der Kategorie',
    groupDefault: 'Neue Kategorie',
    groupColor: 'Farbe',
    deleteGroup: 'Kategorie löschen',
    deleteGroupConfirm: (name: string) => `Die Kategorie „${name}“ löschen? Die Entitäten darin bleiben erhalten.`,
    groupHint: 'Ziehst du den Rahmen, wandern die Entitäten darin mit. An der Ecke unten rechts änderst du seine Größe.',
    members: (n: number) => (n === 1 ? '1 Entität in dieser Kategorie' : `${n} Entitäten in dieser Kategorie`),
    zoom: ['Fern', 'Mittel', 'Nah'],
    copies: (n: number) => (n === 1 ? '1 Kopie' : `${n} Kopien`),
    deleteEntry: 'Entität löschen',
    deleteEntryConfirm: (name: string, copies: number) =>
      copies > 0 ? `„${name}“ samt ${copies} Kopien und deren Inhalt löschen?` : `„${name}“ löschen?`,
  },
  /** Import in die Bibliothek aus einer anderen eigenen Geschichte (Knopf unten in der linken Leiste) */
  libraryImport: {
    button: 'Importieren',
    buttonHint: 'Personen, Orte und weitere Einträge aus einer anderen Geschichte importieren',
    title: 'Aus einer anderen Geschichte importieren',
    pickProject: 'Aus welcher Geschichte?',
    noProjects: 'Es gibt noch keine andere Geschichte, aus der du importieren könntest.',
    back: '← Andere Geschichte',
    search: 'Einträge suchen …',
    empty: (project: string) => `In „${project}“ gibt es noch keine Einträge in der Bibliothek.`,
    none: 'Nichts gefunden.',
    wholeClass: (label: string) => `Alle ${label} auswählen`,
    expand: (label: string) => `${label} aufklappen`,
    collapse: (label: string) => `${label} zuklappen`,
    picked: (n: number, of: number) => `${n} von ${of}`,
    untitled: 'Ohne Namen',
    submit: (n: number) => (n === 0 ? 'Importieren' : `Importieren (${n})`),
    confirmTitle: 'Als Kopien importieren',
    confirm: (n: number, project: string) =>
      `${n === 1 ? 'Die Entität wird' : `Die ${n} Entitäten werden`} als Kopie${n === 1 ? '' : 'n'} aus „${project}“ in diese Geschichte übernommen. Änderungen an den Kopien verändern die Originale nicht – und umgekehrt.`,
    confirmSkip: (names: string[]) => `Übersprungen werden, weil es sie hier schon gibt: ${names.join(', ')}.`,
    confirmNothing: 'Alle gewählten Entitäten gibt es hier schon – es wird nichts importiert.',
    ok: 'OK',
    done: (n: number) => (n === 0 ? 'Nichts importiert.' : n === 1 ? '1 Entität importiert.' : `${n} Entitäten importiert.`),
    skipped: (names: string[]) => `Übersprungen (gibt es hier schon): ${names.join(', ')}.`,
    // aus einer Episode derselben Serie: Referenzen (PLAN.md §43)
    asReference: (n: number) => `Episode ${n} dieser Serie · als Referenz`,
    present: 'schon da',
    presentHint: 'Steht schon als Referenz bzw. Original in dieser Geschichte.',
    confirmRefTitle: 'Als Referenzen übernehmen',
    confirmRef: (n: number, project: string) =>
      `${n === 1 ? 'Die Entität kommt' : `Die ${n} Entitäten kommen`} als Referenz${n === 1 ? '' : 'en'} aus „${project}“ in diese Episode: mit demselben Steckbrief – eine Änderung gilt in allen Episoden – und ihrem Stand am Ende der früheren Episoden.`,
    doneRef: (n: number) => (n === 0 ? 'Nichts übernommen.' : n === 1 ? '1 Referenz übernommen.' : `${n} Referenzen übernommen.`),
  },
  details: {
    back: 'Zurück',
    backTo: (title: string) => `Zurück zu „${title}“`,
    collapse: 'Seitenleiste einklappen',
    expand: 'Seitenleiste ausklappen',
    pick: 'Wähle einen Eintrag, um ihn hier zu bearbeiten.',
    /** Abschnitt einer Kopie (Person in einer Szene, Karte auf der Fläche) */
    inNode: (title: string) => `In „${title}“`,
    onCanvas: 'Auf der Fläche',
    copyHint: 'Gilt nur hier. Bild und Felder gehören zur Entität und gelten überall.',
    removeFrom: (title: string) => `Aus „${title}“ nehmen`,
    removeFromCanvas: 'Von der Fläche nehmen',
    removeCopyConfirm: (name: string, where: string) => `„${name}“ aus „${where}“ nehmen? Die Entität bleibt in der Bibliothek.`,
    removeFromCanvasConfirm: (name: string) => `„${name}“ von der Fläche nehmen? Die Entität bleibt in der Bibliothek.`,
    legacyFields: 'Ältere Angaben nur an dieser Stelle',
    legacyHelp: 'Angaben, die nur für diese Stelle gelten, gehören heute zum Zustand der Szene oder als Feld zur Entität.',
    nothing: 'Wähle etwas auf der Zeichenfläche, um es zu bearbeiten.',
    nothingHint: 'Ein Doppelklick öffnet es.',
    refNone: '— keine Angabe —',
    kind: { story: 'Geschichte', timeline: 'Zeitlinie', event: 'Ereignis', period: 'Zeitraum', instance: 'Entität', normal: 'Metadaten', manuscript: 'Manuskript', section: 'Abschnitt', chapter: 'Kapitel', scene: 'Szene' },
    extraFields: 'Zusätzliche Felder (nur dieser Eintrag)',
    dynamicHelp: 'Nur an dieser Kopie, z. B. Stimmung oder Zustand in genau dieser Szene.',
    addDynamic: 'Feld hinzufügen',
    dynamicLabel: 'Bezeichnung',
    dynamicValue: 'Wert',
    tagColor: 'Eigene Farbe',
    color: 'Farbe',
    colorHint: 'Gilt für die Szene auf der Fläche (Marke bzw. Balken, Vorschaubox) und ihre Karte in der Erzähllinie. Ohne eigene Farbe trägt eine Szene im Kapitel dessen Farbe, sonst die der Klasse.',
    colorDefault: 'Standardfarbe der Klasse',
    colorChapter: 'Farbe des Kapitels',
    colorReset: 'Eigene Farbe aufheben',
    colorResetTo: (to: string) => `Eigene Farbe aufheben – zurück zur ${to}`,
    colorCustom: 'Eigene Farbe wählen',
    noTag: 'keine',
    /** Tabs der Seitenleiste (Szene, Entität) */
    tabs: 'Bereiche',
    tabInfo: 'Info',
    tabTime: 'Zeit',
    tabTimeHint: 'Wann die Szene spielt und wo sie auf der Zeitlinie liegt',
    tabManuscript: 'Manuskript',
    tabManuscriptHint: 'Kapitel, Status und Texte der Szene',
    tabObjects: 'Entitäten',
    tabObjectsHint: 'Wer und was in der Szene vorkommt und was sich hier ändert',
    tabMore: 'Mehr',
    tabMoreCopyHint: 'Was nur an dieser Stelle gilt: eigene Farbe und Verbindungen',
    tabHistory: 'Verlauf',
    tabHistoryHint: 'Beziehungen, Lebensdaten, Zustand und Wissen über die Zeit',
    tabRefs: 'Verweise',
    tabRefsHint: 'Verweisschlüssel, Vorkommen im Text und wo die Entität vorkommt',
    menu: (title: string) => `Aktionen für „${title}“`,
    delete: 'Löschen',
    deleteConfirm: (title: string, inner: number) => (inner > 0 ? `„${title}“ samt ${inner === 1 ? '1 Eintrag' : `${inner} Einträgen`} darin löschen?` : `„${title}“ löschen?`),
    foundationalLinks: 'Mit allen Grundlagen (Prämisse, Welt, …) unsichtbar verknüpft.',
    sharedByEntity: 'Gehört zur Entität',
    inside: (n: number) => (n === 1 ? '1 Eintrag darin' : `${n} Einträge darin`),
    image: 'Titelbild',
    imageChoose: 'Bild wählen …',
    imageRemove: 'Bild entfernen',
    imageUploading: 'Bild wird verkleinert und hochgeladen …',
    imageBad: 'Bitte ein JPEG-, PNG- oder WebP-Bild wählen.',
  },
  connections: {
    title: 'Verbindungen',
    none: 'Noch keine Verbindungen.',
    visible: 'sichtbar',
    visibleHint: 'Unsichtbare Verbindungen bleiben erhalten, werden aber nicht gezeichnet.',
    label: 'Beschriftung',
    delete: 'Verbindung löschen',
    deleteConfirm: 'Diese Verbindung löschen?',
    deleteConfirmTo: (other: string) => `Die Verbindung mit „${other}“ löschen?`,
    add: 'Verbinden mit …',
    others: 'Auch an dieser Verbindung',
  },
  timeline: {
    title: 'Zeitlinie',
    start: 'Start',
    end: 'Ende',
    startFlag: 'Start-Fähnchen (Datum und Uhrzeit)',
    endFlag: 'End-Fähnchen (Datum und Uhrzeit)',
    flags: 'Zeit-Fähnchen',
    addFlag: 'Fähnchen setzen',
    flagLabel: 'Beschriftung',
    flagLabelHint: 'Leer = automatisch die Zeit an der Position',
    flagPos: 'Position',
    flagDefault: 'Fähnchen',
    flagMoveHint: 'Ein Fähnchen lässt sich auf der Zeichenfläche verschieben: entlang der Zeitlinie und nach oben oder unten.',
    preview: 'Vorschau',
    previewTime: 'Zeit',
    previewContent: 'Inhalte',
    previewDescription: 'Kurzbeschreibung',
    previewHint: 'Die Vorschaukarte über der Zeitlinie zeigt immer den Titel. Die Karte lässt sich senkrecht verschieben.',
    labelShow: 'Auf der Beschriftung anzeigen',
    showTime: 'Zeiten (Beginn und Ende)',
    showTimeEvent: 'Zeit',
    showImage: 'Bild',
    noImageYet: 'Diese Entität hat noch kein Bild: Wähle oben im Feld „Bild“ eines.',
    labelShowHint: 'Die Beschriftungsbox lässt sich auf der Zeichenfläche senkrecht verschieben; Zeiten stehen links und rechts daneben, das Bild darunter.',
    display: 'Zeitanzeige',
    displayHint: 'Welche Teile der Zeit an Start, Ende, Fähnchen und Zeiträumen angezeigt werden.',
    displayYear: 'Jahr',
    displayMonth: 'Monat',
    displayDay: 'Tag',
    displayTime: 'Uhrzeit',
    addEvent: '+ Szene',
    position: 'Position',
    eventTime: 'Zeitpunkt',
    from: 'Beginn',
    to: 'Ende',
    length: 'Länge',
    growsHint: 'Die Zeitlinie wächst mit ihren Szenen. Start und Ende kannst du hier auch von Hand verschieben.',
    startPos: 'Start (Achsenwert)',
    endPos: 'Ende (Achsenwert)',
    overlapHint: 'Überlappende Zeiträume und Ereignisse bekommen eine eigene Schiene: Die Zeitlinie teilt sich dann auf.',
    lanes: (n: number) => (n === 1 ? '1 Schiene' : `${n} Schienen`),
    startTime: 'Beginn bzw. Zeitpunkt',
    endTime: 'Ende',
    derivedTime: 'Zeit (legt die Stelle auf der Zeitlinie fest)',
    positionHelp: 'Achsenwert; verschiebt die Szene auf der Zeitlinie.',
    eventDefault: 'Ereignis',
    periodDefault: 'Zeitraum',
    lastTimeline: 'Eine Geschichte hat immer mindestens eine Zeitlinie.',
    main: 'Hauptzeitlinie',
    mainHint: 'Die älteste Zeitlinie der Geschichte: Weitere Zeitlinien lassen sich an sie koppeln. Sie lässt sich nicht löschen.',
    extra: 'Weitere Zeitlinie',
    moveHint: 'Verschieben: die Linie greifen und ziehen.',
    moveHintCoupled: 'Gekoppelt: Lage und Maßstab folgen der Hauptzeitlinie; ziehen lässt sie sich nur nach oben oder unten.',
    couple: 'An Hauptzeitlinie koppeln',
    coupleHint: 'Schiebt die Zeitlinie waagerecht unter dieselben Zeiten der Hauptzeitlinie und gleicht den Maßstab an – die Szenen behalten ihre Zeit. Danach folgt sie der Hauptzeitlinie.',
    uncouple: 'Entkoppeln',
    coupledLength: 'Gekoppelt: Start und Ende bestimmen die Länge, der Maßstab ist der der Hauptzeitlinie.',
    reckoning: 'Zeitrechnung',
    reckoningDate: 'Datum und Uhrzeit',
    reckoningCount: 'Eigene Zählung',
    reckoningHint: 'Eigene Zählung: Start und Ende als Zahl in Minuten, Stunden, Tagen oder Jahren (z. B. Tag 0 bis Tag 30) – ohne Kalender. Hat der Nullpunkt eine Uhrzeit, zeigen Tage die echte Uhrzeit.',
    unit: 'Einheit',
    units: { minutes: 'Minuten', hours: 'Stunden', days: 'Tage', years: 'Jahre' } as Record<'minutes' | 'hours' | 'days' | 'years', string>,
    countStart: 'Start',
    countEnd: 'Ende',
    zero: 'Nullpunkt (Datum, optional)',
    zeroHint: 'Mit Nullpunkt lässt sich die Zählung mit Datumsangaben vergleichen und an eine datierte Hauptzeitlinie koppeln.',
    displayDayOfYear: 'Tag im Jahr',
    displayMinuteOfHour: 'Minuten',
  },
  types: {
    title: 'Typen',
    intro: 'Eigene Typen erweitern die Bibliothek, z. B. „Waffen“. Vordefinierte Typen kannst du nur umfärben.',
    builtin: 'vordefiniert',
    new: 'Neuer Typ',
    name: 'Name',
    category: 'Verhalten',
    categories: {
      object: 'Entität: einmal angelegt, viele Kopien (wie Person)',
      place: 'Ort: einmal angelegt, viele Kopien (wie Ort)',
      normal: 'Normaler Eintrag: frei kopierbar (wie Metadaten)',
    },
    color: 'Farbe',
    placeholder: 'Standardbild der Klasse',
    placeholderHelp: 'Erscheint in Karten und Vorschau-Kacheln, wenn ein Eintrag kein eigenes Titelbild hat.',
    textual: 'Mit Textdokumenten (.md), die sich im Texteditor öffnen lassen',
    fields: 'Felder',
    addField: 'Feld hinzufügen',
    fieldLabel: 'Bezeichnung',
    fieldType: 'Art',
    fieldTypes: { text: 'Text', longtext: 'Langer Text', number: 'Zahl', date: 'Datum', image: 'Bild' },
    titleField: 'Titel-Feld',
    hoverField: 'Hover-Nachricht',
    imageField: 'Titelbild',
    noneField: '– keins –',
    inUse: 'wird verwendet',
    deleteConfirm: (n: string) => `Typ „${n}“ löschen?`,
    needsField: 'Mindestens ein Feld wird gebraucht.',
    needsTitle: 'Wähle ein Text-Feld als Titel.',
  },
  documents: {
    title: 'Texte',
    brief: 'Kurzbeschreibung',
    help: 'Markdown-Dokumente: Öffnen zeigt sie im Manuskript; weitere lassen sich anlegen.',
    open: 'Öffnen',
    add: 'Textdokument hinzufügen',
    standard: 'Standard',
    empty: 'leer',
    words: (n: number) => (n === 1 ? '1 Wort' : `${n} Wörter`),
  },
  /** Exportieren (PLAN.md §29): Manuskript als Word-Datei, Projekt-Sicherung */
  exporting: {
    button: 'Exportieren',
    buttonHint: 'Das Manuskript als Word-Datei (auch als Normseite), als Markdown oder als Klartext bzw. das ganze Projekt als Sicherung herunterladen',
    title: 'Exportieren',
    format: 'Was',
    formats: {
      docx: { label: 'Manuskript als Word-Datei (DOCX)', hint: 'Mit Titelseite, jedem Kapitel auf neuer Seite und „* * *“ zwischen den Szenen.' },
      normseite: { label: 'Manuskript als Normseite (DOCX)', hint: 'Für Verlage und Agenturen: 30 Zeilen à 60 Anschläge in Courier New.' },
      markdown: { label: 'Manuskript als Markdown (MD)', hint: 'Der Text, wie er geschrieben ist (fett, kursiv, Listen), mit Überschriften für Teile und Kapitel.' },
      text: { label: 'Manuskript als Klartext (TXT)', hint: 'Nur der Text, ohne Markierungen – zum Einfügen in andere Programme.' },
      backup: { label: 'Projekt-Sicherung (ZIP)', hint: 'Das ganze Projekt mit Texten, Bildern und Versionen – in „Meine Geschichten“ mit „Sicherung einlesen“ wiederherstellbar.' },
    } as Record<'docx' | 'normseite' | 'markdown' | 'text' | 'backup', { label: string; hint: string }>,
    scope: 'Umfang',
    scopeHint: 'Nicht eingeordnete Szenen bleiben draußen; Verweise und Zeitmarken stehen als ihr Wortlaut da.',
    whole: 'Ganzes Manuskript',
    parts: 'Abschnitte',
    section: (n: number) => `Abschnitt ${n}`,
    chapters: 'Kapitel',
    scenes: 'Szenen',
    scene: (label: string, title: string) => (title ? `${label} ${title}` : `Szene ${label}`),
    chapter: (n: number, title: string) => (title ? `${n}. ${title}` : `Kapitel ${n}`),
    download: 'Herunterladen',
  },
  /** Filmansicht (PLAN.md §35): die Handlung Bild für Bild */
  film: {
    button: 'Film',
    buttonHint: 'Die Geschichte als Film: Bild für Bild, wie sie erzählt wird – ganz oder nur eine Figur –, darüber die Zeitlinie',
    title: 'Film',
    titleOf: (name: string) => `Film: ${name}`,
    filmOf: (name: string) => `Film von ${name}: nur die Szenen, in denen ${name} vorkommt`,
    count: (i: number, n: number) => `${i} / ${n}`,
    figure: 'Figur',
    figureHint: 'Den Film einer Figur zeigen: nur die Szenen, in denen sie vorkommt (Perspektive, spricht, anwesend)',
    allScenes: 'Die ganze Geschichte',
    figureOption: (title: string, n: number) => `${title} (${n === 1 ? '1 Szene' : `${n} Szenen`})`,
    emptyFigure: (name: string) => `${name} tritt in keiner eingeordneten Szene auf.`,
    role: (name: string, roles: string) => `${name}: ${roles}`,
    check: 'Prüfen',
    checkHint: 'An jedem Bild die Widersprüche der Szene zeigen',
    close: 'Film schließen (Esc)',
    empty: 'Noch keine Szene ist in ein Kapitel eingeordnet.',
    first: 'Zum Anfang (Pos1)',
    prev: 'Voriges Bild',
    next: 'Nächstes Bild',
    play: 'Abspielen',
    pause: 'Pause',
    tempo: 'Tempo',
    seconds: (n: number) => `${n} s je Bild`,
    label: (label: string, chapter: string) => (chapter ? `${label} · ${chapter}` : label),
    flashback: 'Rückblende',
    flashforward: 'Vorausdeutung',
    lookback: 'Rückblick',
    resume: 'Hier hast du zuletzt geschrieben',
    noTime: 'ohne Zeit',
    gap: (g: TimeGap): string => {
      if (g.dir === 'same') return { year: 'im selben Jahr', month: 'im selben Monat', day: 'am selben Tag', hour: 'zur selben Stunde', minute: 'zur selben Zeit' }[g.unit];
      return `${gapAmount(g)} ${g.dir === 'later' ? 'später' : 'früher'}`;
    },
    where: 'Wo',
    pov: 'Perspektive',
    who: 'Wer',
    more: (n: number) => `+ ${n} weitere`,
    changes: 'Was sich ändert',
    issues: 'Widersprüche',
    toScene: 'Zur Szene',
    sidePick: 'Rechts zeigen',
    sideHint: 'Rechts die Zusammenfassung, den Szenentext zum Überfliegen oder das Material der Szene – bleibt beim Weiterblättern',
    sides: { summary: 'Zusammenfassung', text: 'Szenentext', material: 'Material' } as Record<'summary' | 'text' | 'material', string>,
    emptySummary: 'Kurzbeschreibung, Ziel, Konflikt und Ausgang sind noch leer.',
    emptyText: 'Der Szenentext ist noch leer.',
    emptyMaterial: 'Zu dieser Szene gibt es kein Material.',
    words: (n: number) => (n === 1 ? '1 Wort' : `${n.toLocaleString('de-DE')} Wörter`),
    axis: 'Zeitlinie: die Szenen in Weltzeit, die jetzige markiert, der Bogen kommt von der vorigen',
    tick: (title: string, when: string) => (when ? `${title} · ${when}` : title),
    background: 'Hintergrundereignisse',
    backgroundOf: (title: string, when: string) => `${title} · Hintergrundereignis${when ? ` · ${when}` : ''}`,
    cut: (g: TimeGap) => `${gapAmount(g)} ohne Szene – nicht maßstäblich`,
    untimed: (n: number) => (n === 1 ? '1 ohne Zeit' : `${n} ohne Zeit`),
    untimedHint: 'Szenen ohne vergleichbare Zeit stehen nicht auf der Zeitlinie',
  },
  /** Szenentext und Material (PLAN.md §36): genau ein Text je Szene; was man dazu sammelt, ist Material */
  material: {
    sceneText: 'Szenentext',
    sceneTextHint: 'Der Text dieser Szene – genau einer. Aus ihm kommen Zeit, Ort, Wissen und alle Prüfungen, er geht in den Export. Frühere Stände hältst du mit Versionen fest.',
    title: 'Material',
    hint: 'Was du zur Szene sammelst: Ideen, Recherche, Links und Bilder. Material zählt für keine Prüfung und kommt nicht in den Export.',
    button: 'Material',
    buttonHint: 'Ideen, Recherche, Links und Bilder zu dieser Szene – neben dem Szenentext',
    close: 'Material schließen',
    resize: 'Breite des Materials',
    resizeHint: 'Ziehen ändert die Breite, Doppelklick stellt sie zurück',
    count: (n: number) => (n === 1 ? '1 Eintrag Material' : `${n} Einträge Material`),
    collapse: 'Einklappen',
    expand: 'Aufklappen',
    collapseAll: 'Alle einklappen',
    expandAll: 'Alle aufklappen',
    move: (title: string) => `„${title}“ verschieben`,
    moveHint: 'Ziehen oder mit ↑ ↓ verschieben',
    kinds: { note: 'Idee', link: 'Link', image: 'Bild' } as Record<'note' | 'link' | 'image', string>,
    addNote: 'Idee',
    addNoteHint: 'Eine Idee, Notiz oder Recherche festhalten',
    addLink: 'Link',
    addLinkHint: 'Eine Webseite merken: Quelle, Vorbild, Recherche',
    addImage: 'Bild',
    addImageHint: 'Ein Foto oder Bild hochladen',
    uploading: 'Lädt …',
    titlePlaceholder: 'Titel',
    textPlaceholder: 'Idee, Notiz, Recherche …',
    urlLabel: 'Webadresse',
    urlPlaceholder: 'z. B. de.wikipedia.org/wiki/Valparaíso',
    linkTitleLabel: 'Titel des Links',
    add: 'Hinzufügen',
    cancel: 'Abbrechen',
    badUrl: 'Das ist keine Webadresse (http oder https).',
    editLink: 'Link bearbeiten',
    openLink: (host: string) => `${host} in neuem Tab öffnen`,
    remove: (kind: string, title: string) => `${kind}${title ? ` „${title}“` : ''} löschen`,
    removeConfirm: (title: string) => `„${title}“ löschen?`,
  },
  /** Versionen der Textdokumente (PLAN.md §28, wie die Snapshots in Scrivener) */
  snapshots: {
    title: 'Versionen',
    button: 'Versionen',
    buttonHint: 'Versionen dieses Textes: Stände sichern, lesen, vergleichen und zurücksetzen',
    take: 'Sichern',
    takeHint: 'Den jetzigen Stand als Version sichern (Strg+Alt+S, gleich mit Titel: Strg+Alt+Umschalt+S)',
    resize: 'Breite der Versionen',
    resizeHint: 'Ziehen ändert die Breite, Doppelklick stellt sie zurück',
    titleDialog: 'Version sichern',
    titleLabel: 'Titel der Version',
    titleHelp: 'Datum und Uhrzeit hält die Version von selbst fest; der Titel ist freiwillig.',
    titlePlaceholder: 'z. B. Vor dem Lektorat',
    save: 'Sichern',
    taken: (when: string) => `Version gesichert (${when})`,
    show: 'Zeigen',
    untitled: 'ohne Titel',
    renameHint: 'Doppelklick: umbenennen',
    remove: 'Löschen',
    removeHint: 'Die gewählte Version löschen',
    removeConfirm: (label: string) => `Die Version „${label}“ löschen? Das lässt sich nicht rückgängig machen.`,
    compare: 'Vergleichen',
    compareHint: 'Zeigt, was sich seit der gewählten Version geändert hat – mit zwei gewählten Versionen (Strg+Klick), was zwischen beiden',
    rollback: 'Zurücksetzen',
    rollbackHint: 'Den Text auf die gewählte Version zurücksetzen',
    rollbackTitle: 'Auf diese Version zurücksetzen?',
    rollbackText: (doc: string, label: string) => `Der jetzige Text von „${doc}“ wird durch die Version „${label}“ ersetzt. Sicherst du ihn vorher, bleibt er als Version erhalten.`,
    rollbackKeep: 'Jetzigen Stand sichern und zurücksetzen',
    rollbackOnly: 'Nur zurücksetzen',
    rolledBack: (label: string) => `Zurückgesetzt auf „${label}“`,
    beforeRollback: (label: string) => `Vor dem Zurücksetzen auf „${label}“`,
    empty: 'Noch keine Versionen. „Sichern“ hält den jetzigen Stand mit Datum fest; einen Titel gibt ihm ein Doppelklick.',
    emptyText: 'Diese Version ist leer.',
    pick: 'Wähle eine Version, um sie zu lesen. „Vergleichen“ zeigt, was sich seitdem geändert hat.',
    selectHint: 'Klick wählt eine Version, Strg+Klick eine zweite zum Vergleichen; Doppelklick benennt um',
    loading: 'Lädt …',
    by: 'nach',
    bys: { paragraph: 'Absätzen', sentence: 'Sätzen', phrase: 'Satzteilen', word: 'Wörtern' } as Record<DiffGranularity, string>,
    changes: (n: number) => (n === 0 ? 'Keine Änderungen' : n === 1 ? '1 Änderung' : `${n} Änderungen`),
    prev: 'Vorige Änderung',
    next: 'Nächste Änderung',
    against: (label: string) => `„${label}“ → jetzt`,
    between: (a: string, b: string) => `„${a}“ → „${b}“`,
    count: (n: number) => (n === 1 ? '1 Version' : `${n} Versionen`),
    close: 'Versionen schließen',
  },
  editor: {
    documents: 'Dokumente',
    filter: 'Dokumente filtern …',
    noNodes: 'Szenen stehen oben in der Gliederung; hier erscheinen neben der Geschichte die Einträge eigener Klassen mit Texten.',
    pick: 'Wähle links ein Dokument, um es zu schreiben.',
    looseWhere: 'lose',
    standard: 'Standard',
    addDocument: 'Dokument',
    newDocumentName: 'Name des Dokuments',
    rename: 'umbenennen',
    deleteConfirm: (name: string) => `Das Dokument „${name}“ löschen?`,
    saving: 'Speichert …',
    failed: 'Nicht gespeichert',
    failedHint: 'Der Server war nicht erreichbar oder hat abgelehnt. Der Text bleibt hier und im Browser erhalten und wird gleich erneut gesendet.',
    conflictShort: 'Anderswo geändert',
    conflict: 'Dieser Text wurde inzwischen in einem anderen Fenster oder auf einem anderen Gerät geändert. Welchen Stand möchtest du behalten?',
    keepMine: 'Meinen Stand speichern',
    takeTheirs: 'Den anderen übernehmen',
    restored: 'Ungespeicherter Text von vorhin wiederhergestellt – er wird jetzt gespeichert.',
    showNode: 'Auf der Zeichenfläche zeigen',
    showScene: 'In der Handlung zeigen',
    words: (n: number) => (n === 1 ? '1 Wort' : `${n} Wörter`),
    pageTheme: 'Seite hell oder dunkel',
    pageThemes: { light: 'Helle Seite (Pergament, dunkelgraue Schrift)', dark: 'Dunkle Seite (helle Schrift)' },
    markView: 'Markierungen im Text',
    markViews: {
      all: 'Alle Markierungen: Verknüpftes blau, Vorschläge gelb, Widersprüche rot',
      conflicts: 'Nur Widersprüche: ruhiger schreiben, rot bleibt',
      none: 'Keine Markierungen: ganz ruhig schreiben – die Zähler bleiben, ein Klick darauf zeigt die Stelle',
    },
    lineType: 'Zeilentyp',
    paragraphType: 'Text',
    boldShort: 'Fett (Strg+B): Text markieren, dann klicken',
    italicShort: 'Kursiv (Strg+I): Text markieren, dann klicken',
    undo: 'Rückgängig (Strg+Z)',
    redo: 'Wiederholen (Strg+Y)',
    linkRef: 'Verknüpfen',
    removeLink: 'Verknüpfung entfernen (das Wort bleibt)',
    removeRef: 'Verweis entfernen (der Name bleibt als Text)',
    removeSpan: 'Bezug entfernen',
    linkRefHint: (keys: string) => `Markiertes Wort oder ganze Textstelle mit einer Person, einem Ort, … verknüpfen (Strg+K)\nGleich mit der Art:\n${keys}`,
    richHint: 'Markieren + Verknüpfen (Strg+K, mit Art Strg+⇧+1 … 5) · @ für Verweise · Strg+S speichert',
    richPlaceholder: 'Hier schreiben … Mit @ verweist du auf Personen, Orte und Dinge.',
    toolbar: 'Formatierung',
    headingLevel: (l: number) => `Überschrift ${l}`,
    strike: 'Durchgestrichen',
    bullets: 'Aufzählung',
    numbered: 'Nummerierte Liste',
    quote: 'Zitat',
    text: 'Text',
  },
  guide: {
    title: 'Tutorial',
    step: (n: number, total: number) => `Schritt ${n} von ${total}`,
    next: 'Weiter',
    back: 'Zurück',
    done: 'Fertig',
    end: 'Tutorial beenden',
    endConfirm: 'Das Tutorial beenden? Die Geschichte bleibt erhalten; „Neu beginnen“ auf seiner Karte beginnt jederzeit von vorn.',
    waiting: 'Weiter geht es von selbst, sobald du das erledigt hast.',
    waitingNext: '„Weiter“ wird frei, sobald du das erledigt hast.',
    loading: 'Einen Moment …',
    /** Führung durch den Demo-Roman „Schachnovelle“ (PF 47): die Fehlersuche */
    schach: {
      start:
        'Willkommen zur „Schachnovelle“: Stefan Zweigs Novelle, gekürzt, in fünf Kapiteln mit vierzehn Szenen – unten in der Erzähllinie. Wir haben zwölf Fehler eingebaut, einer davon stammt von Zweig selbst. Dispositio findet sie alle. Ich zeige dir, wo – und mit welchen Übersichten du eine ganze Geschichte im Blick behältst.',
      hints: 'Oben zählt Dispositio die Widersprüche der Geschichte. Öffne die Hinweise.',
      list: 'Jeder Eintrag sagt, was nicht zusammenpasst, und „Anzeigen“ führt zur Stelle. Fang mit dem Wochentag an: Klicke dort auf „Anzeigen“.',
      weekday:
        'Hier ist die Stelle, rot markiert: „Donnerstag, dem 27. Juli“. Der 27. Juli 1938 war ein Mittwoch – so steht es bei Zweig. Klicke auf die Stelle: Dispositio schlägt vor, den Wochentag oder das Datum zu ändern; ✕ merkt sich „ist Absicht“, dann ist der Widerspruch erledigt. Bei Zweigs Text bleibt es, wie es ist. Im Prüfbericht hakt das Kästchen vor einem Eintrag ihn ab, ohne etwas zu ändern.',
      back: 'Zurück zur Handlung: Klicke auf „Handlung“.',
      dots: 'In der Erzähllinie trägt jede Szene mit einem Widerspruch einen roten Punkt mit der Zahl ihrer Widersprüche. Ein Klick auf den Punkt nennt sie und führt hin.',
      film: 'Noch schneller geht es im Film: Er zeigt die Geschichte Bild für Bild. Öffne ihn mit dem Knopf mit dem Bildstreifen.',
      check: 'Schalte oben rechts „Prüfen“ ein: Dann steht an jedem Bild, was in dieser Szene nicht stimmt.',
      filmClose: 'Der Film ist eine von mehreren Übersichten. Schließe ihn jetzt (✕ oben rechts oder Esc) – in der Erzähllinie warten weitere.',
      matrix: 'Wer tritt wann auf? Öffne oben in der Erzähllinie die Auftrittsmatrix: den Knopf mit dem Raster.',
      matrixRead:
        'Die Auftrittsmatrix stellt die Szenen in Lesereihenfolge gegen die Personen: Die Zielscheibe steht für Perspektive, das Quadrat für „spricht“, der Punkt für „anwesend“, der Ring für „erwähnt“. Je Person zählt sie die Auftritte und die längste Lücke; ab drei Szenen ist sie getönt. So fällt auf, dass McConnor nach „Der Fremde“ sieben Szenen lang verschwindet. Oben schaltest du auf Weltzeit oder auf andere Bereiche um; ein Klick auf eine Szene wählt sie, ein Klick auf eine Person öffnet sie in der Seitenleiste.',
      filter: 'Wo begegnen sich zwei Figuren? Dafür gibt es den Filter. Schließe die Matrix mit ✕ und öffne oben in der Erzähllinie den Filter: den Knopf mit dem Trichter.',
      filterPick: 'Wähle zwei Personen: Suche „Czentovic“ und klicke auf „Mirko Czentovic“, dann suche „Dr. B.“ und klicke darauf.',
      filterDone:
        'Mit „Alle (UND)“ sind jetzt nur die Szenen hervorgehoben, in denen beide vorkommen – auf der Erzähllinie und auf der Zeitlinie, alle anderen sind blass. „Mindestens eins (ODER)“ zeigt jede Szene, in der eine der gewählten Entitäten vorkommt, etwa: Was passiert alles im Rauchsalon? Hebe den Filter mit „Filter aufheben“ wieder auf und schließe das Fenster.',
      knowledge: 'Wer weiß was seit wann? Öffne die Wissensübersicht: den Knopf mit der Glühbirne.',
      knowledgeRead:
        'Hier stehen Informationen gegen Figuren und Leser: wer seit welcher Szene was weiß. In der „Schachnovelle“ ist es die Schachvergiftung – der Leser erfährt früher davon als Dr. B. und der Erzähler. Das ist dramatische Ironie, darum sind die Zellen getönt. Ebenso getönt werden Rätsel (die Figur weiß es früher) und was dem Leser nie enthüllt wird. Schließe die Übersicht wieder mit ✕.',
      structure: 'Bleibt der Aufbau des Buchs. Öffne das Strukturfenster: den Knopf mit den drei Rauten.',
      structureRead:
        'Das Strukturfenster legt die Story Beats über das Buch – hier „Drei Akte“: je Beat das Soll laut Vorlage (◇) und das Ist an seiner ersten Szene (◆), darunter die Kapitel nach Wörtern, die Akte als Linien und die Glückskurve aus den Wertverschiebungen der Szenen. „Weltzeit“ ordnet nach der erzählten Welt: Dort ist der Mittelpunkt getönt, weil „Der Mantel“ eine Rückblende ist. Oben wählst du die Liste – „Dr. B.s Weg“ verfolgt eine Figur. Fehlt ein Beat, meldet es der Prüfbericht unter „Struktur“. Schließe das Fenster mit ✕.',
      drag: 'Beim Umbauen hilft dir Dispositio schon vorher: Ziehst du eine Szene auf der Zeitlinie oder sortierst sie in der Erzähllinie um, steht unterwegs da, was danach nicht mehr passt (⚠), was sich auflöst (✓) und welche Szenen mitwandern (↷). Und ruht die Maus kurz auf einer Szene, zeigt ein Übersichtsfenster das Wichtigste auf einen Blick.',
      end: 'Jetzt du: Finde die übrigen Fehler – mit den Hinweisen, den roten Punkten, dem Film und den Übersichten. Es geht um Wissen, Orte, Zeiten, Besitz und Perspektive. Die Lösung steht in der Notiz „Die eingebauten Fehler“ auf der Handlungsfläche. Schau erst hinein, wenn du nicht weiterkommst. Wie du selbst verknüpfst und Beziehungen anlegst, zeigt das Tutorial „Verknüpfungen und Beziehungen“.',
    },
    /** Tutorial „Verknüpfungen und Beziehungen“ an der „Schachnovelle“ (ohne eingebaute Fehler) */
    links: {
      start:
        'Willkommen! An der „Schachnovelle“ lernst du, wie du deinen Text mit deiner Geschichte verknüpfst: Figuren, Orte und Wendungen im Text, Bezüge (wer spricht, handelt, denkt), Wissen und Zeit im Text – danach Beziehungen zwischen Figuren und den Tod einer Figur. Diese Fassung hat keine eingebauten Fehler, dafür ein paar Lücken, die du selbst füllst.',
      open: 'Wechsle in den Modus „Manuskript“ und öffne links im Binder die Szene 2.1 „Die Falle“ im Kapitel „Im Rauchsalon“.',
      plain:
        '„Die Falle“ ist noch gar nicht verknüpft – so, als hättest du den Text gerade abgetippt. Gelb markiert sind mögliche Verweise: Namen und Wörter, die Dispositio schon kennt. Oben über der Seite zählt es sie. Wir verknüpfen jetzt Schritt für Schritt.',
      name: 'Die einfachste Verknüpfung: Ein Wort ist eine Entität. Markiere im zweiten Absatz „McConnor“, klicke oben auf „Verknüpfen“ (oder Strg+K), lass „Ist …“ gewählt und wähle „McConnor“. Das Wort wird blau – und McConnor kommt in die Szene.',
      phrase:
        'Auch mehrere Wörter lassen sich verknüpfen, so wie sie im Text stehen: Markiere im ersten Absatz „meiner Frau“ und verknüpfe es ebenso mit „Die Frau des Erzählers“. Dispositio merkt sich die Wendung als Vorkommen und erkennt sie künftig überall wieder.\n\nEs geht auch ohne Markieren: Tippst du direkt an ein Wort ein „@“, öffnet sich derselbe Browser.',
      term: '„Smoking Room“ ist gelb: ein Vorkommen von „Der Rauchsalon“, das schon an der Entität steht. Klicke hinein und übernimm den Vorschlag mit ✓ (Alt+Enter). ✕ (Alt+Rücktaste) merkt sich, dass die Stelle bewusst nicht verknüpft ist. Oben über der Seite nimmst du viele Vorschläge auf einmal an.',
      objects:
        'Rechts im Tab „Entitäten“ stehen jetzt McConnor, die Frau des Erzählers und der Rauchsalon: Was du im Text verknüpfst, kommt von selbst in die Szene – und geht wieder, wenn du den letzten Verweis löschst. Der Rauchsalon ist als erster verknüpfter Ort zugleich Schauplatz der Szene (Tab „Info“); nennt der Text später einen anderen Ort, schlägt Dispositio einen Ortswechsel vor. Die Vorkommen einer Entität pflegst du an ihr selbst, im Tab „Verweise“.',
      span: 'Die zweite Art: Eine ganze Stelle gehört zu einer Entität. Markiere den letzten Satz „Als er die erste Partie verlor, … geschehen sein.“, klicke auf „Verknüpfen“, wähle „Gehört zu …“ und dann „Handlung“. Wähle „McConnor“ und klicke auf „Zuordnen“.',
      spanDone:
        'Am Rand steht jetzt eine Linie mit „McConnor“: Diese Stelle ist seine Handlung. Neben „Handlung“ gibt es „Rede“, „Gedanke“ und „Beschreibung“. Daraus weiß Dispositio, wer spricht und wer anwesend ist – die Auftrittsmatrix zeigt es –, und es meldet, wenn eine Szene die Gedanken einer Figur wiedergibt, aus deren Sicht sie gar nicht erzählt wird. Ein Klick auf das Schild ändert oder löst den Bezug.',
      timeMark:
        'Jetzt die Zeit im Text: „zur Stunde“ meint den Nachmittag des 2. Juni 1939. Markiere im ersten Absatz „zur Stunde“ und klicke oben auf „Zeit“. Tippe „2.6.1939 16:00“ ein – darunter steht, was das für die Szene heißt – und klicke auf „Setzen“.',
      timeDone:
        'Die Stelle ist jetzt eine Zeitmarke: „Die Falle“ hat ihre Zeit aus dem Text und steht damit auf der Zeitlinie „Die Überfahrt“. Ein Klick auf die Marke ändert sie – als Beginn, Ende oder Dauer der Szene, mit Genauigkeit und „ungefähr“.',
      openDollar: 'Öffne im Binder die nächste Szene, 2.2 „Zweihundertfünfzig Dollar“.',
      timeSuggest: 'Meist geht es ohne Markieren, denn Dispositio erkennt Zeitangaben im Text: „Am nächsten Nachmittag“ ist gestrichelt unterstrichen. Klicke hinein und übernimm den Vorschlag mit ✓.',
      timeRel:
        '„Am nächsten Nachmittag“ ist eine relative Angabe: Dispositio rechnet von der vorigen Szene mit Zeit aus – von „Die Falle“, also der 3. Juni um 15 Uhr. Verschiebst du „Die Falle“, wandert diese Szene mit. Erkannt werden auch Daten („am 3. März 1823“), Uhrzeiten („gegen halb neun“), Tageszeiten, Enden („bis zum Abend“) und Dauern („drei Tage lang“).',
      openHospital: 'Weiter zu Dr. B.: Öffne im Binder die Szene 4.3 „Im Hospital“.',
      learn:
        'Wissen ist eine besondere Verknüpfung. Hier erfährt Dr. B., was mit ihm geschehen ist: die Schachvergiftung. Markiere im zweiten Absatz den Satz „Zwei Tage später erklärte mir … vorgefallen war.“, klicke auf „Verknüpfen“, wähle „Gehört zu …“ und unter „Wissen“ „Erfährt“. Wähle „Dr. B.“ und „Die Schachvergiftung“ und klicke auf „Zuordnen“.',
      learnDone:
        'Jetzt weiß Dispositio: Dr. B. kennt die Schachvergiftung ab dieser Szene. Damit ist auch ein Hinweis verschwunden – in „Nur eine Partie“ sprach Dr. B. davon, ohne es zu wissen. „Enthüllt“ markiert entsprechend, wo der Leser etwas erfährt; die Wissensübersicht in der Erzähllinie stellt alles gegenüber.',
      toLibrary: 'Nun zu den Beziehungen. Wechsle in die Bibliothek.',
      openFather: 'Klicke auf „Der Donauschiffer“ – Mirko Czentovics Vater.',
      family:
        'Rechts ist der Tab „Verlauf“ offen: Beziehungen und Zustand über die Zeit. Beziehungen legst du in eigenen Worten an. Tippe in die Eingabezeile „Vater von Mirko Czentovic“ und drücke Enter – schon beim Tippen steht darunter, was daraus wird.',
      familyDone:
        'Die Beziehung gilt in beiden Richtungen: Sie steht auch bei Czentovic. Aus Eltern und Geschwistern leitet Dispositio weitere Verwandte ab – Großeltern, Onkel, Cousinen – und prüft, ob es passt, etwa ob ein Elternteil älter ist als das Kind.',
      death: 'Der Donauschiffer stirbt, als ein Getreidedampfer seine Barke überrennt – das ist das Hintergrundereignis „Der Getreidedampfer“ im Jahr 1930. Tippe „stirbt in Der Getreidedampfer“ und drücke Enter.',
      deathDone:
        'Ab dieser Szene ist der Donauschiffer tot, und seine Lebensdaten stehen da. Ein Tod gehört in die Szene, in der er geschieht. Du kannst ihn auch dort eintragen – in der Szene unter „Änderungen in dieser Szene“: „Der Donauschiffer stirbt“ – oder im Text: Steht „stirbt“ oder „tot“ neben einer verknüpften Figur, schlägt Dispositio die Änderung vor, und ✓ übernimmt sie.',
      openDrb: 'Noch eine Beziehung, diesmal mit Zeit und Haltung. Klicke auf „Dr. B.“.',
      enemy: 'In der letzten Szene, „Remember“, werden Dr. B. und Czentovic zu Feinden. Tippe „verfeindet mit Mirko Czentovic ab Remember“ und drücke Enter. Mit „ab“, „bis“ und dem Titel einer Szene gilt eine Beziehung nur für diesen Teil der Geschichte.',
      attitude: 'Wie stehen die beiden zueinander? Unter der Feindschaft wählst du die Haltung je Richtung: links die von Dr. B., rechts die von Czentovic. Stelle Dr. B. auf „abgeneigt“.',
      attitudeDone:
        'Haltungen dürfen sich ändern: „+ Wendepunkt“ hält fest, ab welcher Szene einer anders zum anderen steht. So legst du alle Beziehungen an – Familie, Ehe, Liebe, Freundschaft, Feindschaft, Hierarchie, Mitgliedschaft, bei Orten sogar Reisewege. Dieselbe Eingabezeile steht in jeder Szene.',
      toAbfahrt: 'Was folgt aus dem Tod? Wechsle ins „Manuskript“ und öffne im Binder die erste Szene, 1.1 „Abfahrt“ – sie spielt 1939, neun Jahre danach.',
      deadText:
        'Hier ist die Stelle, rot markiert: Der Donauschiffer winkt am Kai, obwohl er tot ist. So hält Dispositio deine Geschichte stimmig – wer tot ist, tritt nicht mehr auf, höchstens in Rückblenden oder Erinnerungen. Ein Klick auf die Stelle zeigt die Auswege, oben zählt „Hinweise“ den Widerspruch mit. Zweig lässt den Vater übrigens nicht winken; diesen Satz haben wir für die „Fehlersuche“ eingebaut.',
      glance: 'Fahre mit der Maus über das verknüpfte Wort „Donauschiffer“ im zweiten Absatz und lass sie kurz ruhen – oder klicke hinein. Der Element-Kontext zeigt unter „Stand hier“, wie es zu dieser Szene um ihn steht.',
      end: 'Geschafft! Du kennst jetzt alle Arten der Verknüpfung – „Ist …“, Wendungen, Vorkommen, Bezüge, Wissen und Zeitmarken – dazu Beziehungen mit Zeit und Haltung und den Tod einer Figur. Die übrigen Szenen sind schon verknüpft: Schau dir an, wie, und probiere weiter. Wie Dispositio die ganze Geschichte prüft und überblickt, zeigt die „Fehlersuche“.',
    },
    // Rundgang durch die Oberfläche an der „Schachnovelle“ (lib/tourGuide.ts)
    tour: {
      start:
        'Willkommen zum Rundgang! Wir gehen an der „Schachnovelle“ einmal durch Dispositio – sie ist fertig ausgearbeitet, sodass überall etwas zu sehen ist. In der Mitte der Kopfleiste wechselst du zwischen den drei Modi: „Bibliothek“ (wer und was – Figuren, Orte, Gegenstände), „Handlung“ (wann – die Szenen in der Zeit) und „Manuskript“ (der Text). Wir gehen sie von links nach rechts durch; „Zurück“ zeigt eine Erklärung noch einmal.',
      barLeft:
        'Links: Das Logo führt zurück zur Übersicht deiner Geschichten. ↶ und ↷ nehmen die letzte Änderung zurück bzw. wiederholen sie (Strg+Z, Strg+Y) – jede Änderung, auch ein Löschen. In der Handlung kommt ⤢ dazu: Es zeigt die ganze Fläche.',
      barRight:
        'Rechts: Die Suche findet Szenen, Entitäten und Textstellen; mit „@“ suchst du nach Verweisen. Daneben hell und dunkel. „Hinweise“ öffnet den Prüfbericht – wie du damit Widersprüche findest, zeigt das Tutorial „Fehlersuche“. Ganz rechts dein Profil.',
      toLibrary: 'Wechsle in die „Bibliothek“.',
      areas:
        'Die Bibliothek sammelt alles, was in deiner Geschichte vorkommt – links nach Bereichen: Personen, Orte, Landschaften, Gegenstände, Organisationen und Konzepte, jeweils mit ihrer Zahl. Eigene Klassen (etwa „Raumschiffe“) erscheinen hier auch. „Welt“ hält fest, was für die ganze Geschichte gilt, etwa Prämisse und Welt. Mit « klappt die Leiste ein.',
      cards:
        'Jede Person ist eine Karte – hier Dr. B.: Bild, Name, Kurzbeschreibung und in wie vielen Szenen er vorkommt. Ein Klick öffnet eine Karte rechts in der Seitenleiste, Ziehen legt sie in eine Kategorie. Entf löscht die gewählte – nach einer Rückfrage.',
      categories:
        'Kategorien gliedern einen Bereich. Im Kopf: die Farbe, der Name (Doppelklick benennt um), „+ Neue …“ legt einen Eintrag gleich darin an, ✎ und ✕ benennen die Kategorie um bzw. löschen sie – die Einträge bleiben. Den Kopf ziehen ändert die Reihenfolge.',
      toolbar:
        'Oben: „+ Neue Person“ öffnet ein Fenster mit allen Feldern, „+ Kategorie“ legt eine Kategorie an. „Suchen …“ filtert die Karten, − und + ändern ihre Größe. Links davon wechselst du zwischen „Karten“ und „Pinnwand“.',
      board: 'Klicke auf „Pinnwand“.',
      boardView:
        'Auf der Pinnwand liegen dieselben Einträge frei wie auf einem Tisch: Ziehen ordnet sie an, die Rahmen sind die Kategorien – was im Rahmen liegt, gehört dazu. Ein Doppelklick auf eine freie Stelle legt dort einen Eintrag an, das Mausrad zoomt. Dispositio merkt sich die Lage.',
      openEntity: 'Öffne jetzt Dr. B.: Wechsle zurück zu „Karten“ und klicke auf seine Karte – oder klicke ihn auf der Pinnwand an.',
      entityHead:
        'Rechts die Seitenleiste – sie zeigt immer, was gewählt ist. Oben Name und Klasse und in wie vielen Szenen Dr. B. vorkommt; ▶ spielt seine Szenen als Film ab, ⋯ hat Löschen und Ähnliches. Was du hier änderst, ist sofort gespeichert. Darunter drei Tabs.',
      entityInfo:
        '„Info“: das Bild (anklicken oder eine Datei hineinziehen) und die Felder – Name, Kurzbeschreibung, Alter, Herkunft … Jedes Feld hat ein Menü zum Umbenennen und Entfernen, „+ Feld“ fügt eines hinzu; das gilt dann für alle Personen. Unten die Kategorie.',
      entityHistory:
        '„Verlauf“: was sich mit der Zeit ergibt – die Beziehungen (Dr. B. ist mit Czentovic verfeindet), Verwandtschaft, Lebensdaten, sein Zustand über die Zeit (die Narbe) und sein Wissen. Ganz unten die Eingabezeile für Beziehungen und Änderungen in eigenen Worten; wie du sie anlegst, zeigt das Tutorial „Verknüpfungen und Beziehungen“.',
      entityRefs:
        '„Verweise“: Mit dem Schlüssel (@drb) steht Dr. B. im Text. Die Vorkommen sind Wörter, mit denen er im Text vorkommt – damit findet Dispositio Stellen, die noch nicht verknüpft sind. „Verwendung“ zeigt, wo er überall vorkommt.',
      import: '„Importieren“ unten links holt Einträge aus einer anderen deiner Geschichten – als eigenständige Kopie, aus einer Episode derselben Serie als Referenz.',
      toPlanning: 'Wechsle in die „Handlung“.',
      canvas:
        'Die Fläche zeigt die Geschichte in der Zeit: zwei Zeitlinien, „Die Überfahrt“ und „Die Jahre davor“, mit den Szenen als Marken (ein Zeitpunkt) oder Balken (ein Zeitraum). Ziehen verschiebt die Ansicht, das Mausrad zoomt (unten rechts die Zoomstufe), ← und → springen von Szene zu Szene. Eine Szene entlang der Zeitlinie ziehen ändert ihre Zeit.',
      band:
        'Unten die Erzähllinie: das Buch in Lesereihenfolge – Kapitel mit ihren Szenen als Karten. Ziehen ordnet um, ein Doppelklick öffnet den Text einer Szene. Die Linien verbinden jede Karte mit ihrer Stelle auf der Fläche; die Marken für Akte und Beats kommen aus den Story Beats.',
      orders:
        'Das ist der Grundgedanke von Dispositio: Jede Szene hat zwei Plätze – oben, wann sie in der Welt geschieht, unten, wann man sie liest. „Bileams Esel“ steht im ersten Kapitel, spielt aber Jahre früher, auf der Zeitlinie „Die Jahre davor“: Czentovics Kindheit. Das ↶ an der Karte sagt: eine Rückblende. Dispositio erkennt Rückblenden und Vorausdeutungen von selbst.',
      addScene: 'Jetzt du: Leg eine Szene an. Klicke im letzten Kapitel auf „+ Szene“ – sie erscheint als neue Karte am Ende des Kapitels.',
      placeScene:
        'Die neue Szene steht im Buch, aber noch nicht in der Zeit. Ziehe ihre Karte aus der Erzähllinie nach oben auf die Zeitlinie „Die Überfahrt“ und lass sie dort los, wo sie spielen soll.',
      placed: (flashback: boolean) =>
        `Jetzt hat die Szene beides: ihre Stelle im Buch und ihre Zeit – dort, wo du sie abgelegt hast; die Linie verbindet Karte und Marke.${flashback ? ' Weil sie früher spielt als „Remember“, aber danach gelesen wird, ist sie eine Rückblende: ↶ an der Karte.' : ''} Titel und alles Weitere trägst du rechts in der Seitenleiste ein. Ob eine Szene als Marke (Zeitpunkt) oder als Balken (Zeitraum) erscheint, folgt aus ihrer Zeit: Mit einem Ende oder einer Dauer wird sie zum Zeitraum.`,
      tools:
        'Links die Werkzeuge: Zeitlinie, Szene und Notiz – auf die Fläche ziehen oder anklicken; eine Szene auf einer Zeitlinie hat gleich ihre Zeit, ein Kapitel bekommt sie in der Erzähllinie. Darunter die „Ideen“: Notizen ohne Zeit; auf eine Zeitlinie gezogen bekommen sie eine. Der Knopf + oben rechts auf der Fläche legt ebenfalls etwas an.',
      time:
        'Über der Fläche: „Zeitraffer“ staucht lange leere Spannen, damit dichte Stellen Platz bekommen; „Spuren“ zeigt unter der Fläche, wann welche Figur auftritt. Der Streifen daneben ist die ganze Zeit im Überblick – ziehen oder klicken springt dorthin.',
      bandTools:
        'Die Knöpfe der Erzähllinie: Filter, Status, Vorschau und die Übersichten – Auftrittsmatrix, Struktur (Story Beats), Film und Wissensübersicht –, dann die Darstellung – Kapitel nebeneinander, alle Linien, kompakt, Bilder, Kurzbeschreibung – und „+ Kapitel“. Ganz links klappst du die Erzähllinie ein oder ins Vollbild. Was die Übersichten zeigen, erklärt die „Fehlersuche“.',
      selectScene: 'Klicke in der Erzähllinie auf die Szene „Remember“ im letzten Kapitel. Die Fläche fährt zu ihr.',
      sceneInfo:
        'Die Seitenleiste zeigt jetzt die Szene. „Info“: Titel, Kurzbeschreibung, Perspektive (aus wessen Sicht erzählt wird), Schauplatz, Ziel, Konflikt und Ausgang, das Szenenbild, eine Farbe und Verbindungen zu anderen Szenen.',
      sceneTime:
        '„Zeit“: Beginn, Ende oder Dauer, wie genau die Angabe ist und woher sie stammt – aus einer Zeitmarke im Text, von der Lage auf der Zeitlinie oder von Hand. Dazu, an welcher Zeitlinie die Szene hängt.',
      sceneManuscript:
        '„Manuskript“: Kapitel und Stelle im Buch, der Status (von „Idee“ bis „fertig“), die Struktur – welcher Story Beat hier steht und wie die Szene den Wert verschiebt –, dazu Szenentext und Material.',
      sceneObjects:
        '„Entitäten“: wer und was in der Szene vorkommt – aus dem Text verknüpft oder von Hand hinzugefügt. Darunter die Änderungen in dieser Szene (jemand stirbt, reist, bekommt etwas) mit ihrer Eingabezeile.',
      toEditor: 'Doppelklicke in der Erzähllinie auf „Remember“ – das öffnet ihren Text im Manuskript. (Oder oben „Manuskript“ und links im Binder die Szene.)',
      binder:
        'Links der Binder: das Buch als Gliederung – Abschnitte (etwa Akte), Kapitel und Szenen mit Wortzahl und Status; Rauten zeigen Story Beats. Anklicken öffnet, Ziehen ordnet um, beim Überfahren erscheinen ✎ ▲ ▼ ✕. Unten „+ Kapitel“ und „+ Abschnitt“, oben „Exportieren“ und ein Filter. Unter „Weitere Texte“ liegen die Texte der ganzen Geschichte, etwa Pitch und Exposé.',
      page:
        'In der Mitte die Seite mit dem Szenentext. Blaue Wörter sind mit Entitäten verknüpft, die Randlinie links zeigt Bezüge – wer hier spricht, handelt, denkt oder etwas erfährt –, hinterlegte Stellen sind Zeitmarken. Gelb markiert Dispositio mögliche Verweise, rot Widersprüche. Gespeichert wird von selbst. Rechts bleibt die Seitenleiste der Szene: Perspektive, Zeit und Entitäten hast du beim Schreiben im Blick.',
      format:
        'Über der Seite die Werkzeuge: Zeilentyp, fett, kursiv, durchgestrichen, Listen und Zitat; „Verknüpfen“ (Strg+K) verbindet ein markiertes Wort oder eine Stelle mit einer Entität, „Zeit“ setzt eine Zeitmarke. Hat Dispositio Vorschläge, zählt sie ein Knopf daneben. Rechts wählst du, welche Markierungen zu sehen sind.',
      glance:
        'Fahre mit der Maus über ein blaues Wort, etwa „Czentovic“, oder klicke hinein: Der Element-Kontext zeigt den Steckbrief, den Stand in dieser Szene – lebt er, wo ist er, was hat er –, was er hier schon weiß und wo er zuletzt auftrat. „Entität öffnen“ führt in die Seitenleiste.',
      compass: 'Über dem Text der Kompass: welcher Story Beat an dieser Stelle des Buchs steht oder als nächster fällig ist. Aufgeklappt sagt er, was hier geschehen soll.',
      docHead:
        'Ganz oben: Titel, Wörter und ob gespeichert ist. „In der Handlung zeigen“ springt zur Szene auf der Fläche. „Material“ öffnet daneben Ideen, Links und Bilder zur Szene, „Versionen“ sichert Stände des Texts, vergleicht sie und setzt zurück. Daneben hell oder dunkel für die Seite.',
      export: '„Exportieren“ gibt das Manuskript als Word-Datei (auch als Normseite), als Markdown oder reinen Text aus – ganz, je Abschnitt, Kapitel oder Szene – oder als Sicherung der ganzen Geschichte.',
      end: 'Das war der Rundgang. Jetzt ist alles frei – probier aus, was du gesehen hast; ↶ nimmt jede Änderung zurück. Über das Logo geht es zur Übersicht mit zwei weiteren Tutorials: „Verknüpfungen und Beziehungen“ zeigt, wie Text, Zeit und Figuren zusammenkommen, die „Fehlersuche“, wie Dispositio Widersprüche findet (oben „Hinweise“) und was die Übersichten über die ganze Geschichte zeigen.',
    },
  },
  manuscript: {
    outline: 'Gliederung',
    otherTexts: 'Weitere Texte',
    otherTextsHint: 'Pitch und Exposé der Geschichte, Texte eigener Klassen',
    addChapter: 'Kapitel',
    addSection: 'Abschnitt',
    addSectionHint: 'Neuer Abschnitt – ein Teil des Buchs oder ein Akt, der Kapitel zusammenfasst. Kapitel lassen sich hineinziehen.',
    addScene: 'Szene',
    chapterDefault: (n: number) => `Kapitel ${n}`,
    sectionDefault: (n: number) => `Teil ${n}`,
    noSectionChapters: 'Noch kein Kapitel. „+ Kapitel“ legt eins an, oder ziehe eins hierher.',
    deleteSection: 'Abschnitt löschen',
    deleteSectionConfirm: (title: string, chapters: number) =>
      chapters > 0 ? `Den Abschnitt „${title}“ löschen? ${chapters === 1 ? 'Sein Kapitel bleibt' : `Seine ${chapters} Kapitel bleiben`} erhalten (dann direkt im Manuskript).` : `Den Abschnitt „${title}“ löschen?`,
    sceneDefault: 'Neue Szene',
    noChapters: 'Noch keine Kapitel. Lege das erste an.',
    noScenes: 'Noch keine Szene. „+ Szene“ legt eine an, oder ziehe eine hierher.',
    noUnassigned: 'Alle Szenen stehen in einem Kapitel.',
    renameChapter: 'Kapitel umbenennen',
    deleteChapter: 'Kapitel löschen',
    deleteChapterConfirm: (title: string, scenes: number) =>
      scenes > 0
        ? `Das Kapitel „${title}“ löschen? ${scenes === 1 ? 'Seine Szene bleibt' : `Seine ${scenes} Szenen bleiben`} erhalten (dann nicht eingeordnet).`
        : `Das Kapitel „${title}“ löschen?`,
    deleteScene: 'Szene löschen',
    deleteSceneConfirm: (title: string) => `Die Szene „${title}“ samt Text löschen? Sie verschwindet auch von der Planungsfläche.`,
    up: 'nach oben',
    down: 'nach unten',
    drag: 'Ziehen zum Umsortieren · Doppelklick oder F2: umbenennen',
    collapse: (title: string) => `„${title}“ zuklappen`,
    expand: (title: string) => `„${title}“ aufklappen`,
    words: (n: number) => (n === 1 ? '1 Wort' : `${n.toLocaleString('de-DE')} Wörter`),
    pov: (name: string) => `Perspektive: ${name}`,
    setting: (name: string) => `Schauplatz: ${name}`,
    status: { Idee: 'Idee', Entwurf: 'Entwurf', überarbeitet: 'überarbeitet', fertig: 'fertig' } as Record<string, string>,
    place: 'Im Manuskript',
    chapterLabel: 'Kapitel der Szene',
    sceneLabel: (label: string) => `Szene ${label}`,
    flashbackInfo: (label: string) => `↶ Rückblende: spielt vor Szene ${label}, wird aber danach gelesen.`,
    flashforwardInfo: (label: string) => `↷ Vorausdeutung: spielt nach Szene ${label}, wird aber vorher gelesen.`,
    unassignedHelp: 'Noch in keinem Kapitel: Wähle eines, oder zieh die Karte in der Erzähllinie an ihre Stelle.',
    skip: 'Hintergrundereignis (bewusst nicht erzählt)',
    skipHelp: 'Die Szene passiert in der Welt, kommt aber nie ins Manuskript (Vorgeschichte, Geburten, Tode, Kriege). Auf der Zeitlinie schlicht gezeichnet.',
    openScene: 'Szene öffnen',
  },
  line: {
    title: 'Erzähllinie',
    hint: 'Mit der Maus über einer Szenenkarte zeigt sich ihre Verbindung zur Zeitlinie · Szenen lassen sich auf die Zeitlinie ziehen · Doppelklick legt eine neue Szene an',
    emptyHint: 'Ziehe eine Szene von der Zeitlinie in ein Kapitel, um sie einzuordnen – oder doppelklicke für eine neue.',
    unassigned: 'Nicht eingeordnet',
    unassignedHint: 'Szenen ohne Kapitel, in der Reihenfolge der Weltzeit. Hierher ziehen nimmt eine Szene aus ihrem Kapitel.',
    notPlaced: 'Nicht auf der Fläche – zieh die Karte auf die Zeichenfläche, um die Szene dort abzulegen',
    flashback: 'Rückblende (spielt vor der vorigen Szene der Haupthandlung)',
    flashforward: 'Vorausdeutung (spielt nach der nächsten Szene der Haupthandlung)',
    assigned: (title: string, chapter: number) => `„${title}“ steht jetzt in Kapitel ${chapter}.`,
    unassignedNow: (title: string) => `„${title}“ ist nicht mehr eingeordnet.`,
    allLines: 'Alle Linien',
    allLinesHint: 'Verbindungslinien zu allen Szenen auf der Zeichenfläche zeigen',
    flow: 'Kapitel nebeneinander',
    flowHint: 'Kapitel nebeneinander über die ganze Breite, erst dann eine neue Reihe (an: möglichst viele Szenen auf einen Blick; aus: Kapitel untereinander)',
    flowOneRow: 'Erst mit Platz für mehrere Reihen – Band größer ziehen oder Vollbild. In einer Reihe stehen die Kapitel ohnehin nebeneinander.',
    images: 'Bilder',
    imagesHint: 'Karten als Bildkacheln mit dem Szenenbild zeigen',
    description: 'Kurzbeschreibung',
    descriptionHint: 'Kurzbeschreibung der Szene rechts neben der Karte zeigen',
    compact: 'Kompakt',
    compactHint: 'kleine Karten nur mit Farbe, Nummer und Titel – das Band wird schmal (spart Platz auf kleinen Bildschirmen)',
    notCompact: 'nicht bei kompakten Karten – erst „Kompakt“ ausschalten',
    status: 'Status',
    statusHint: 'Status der Szene (Idee, Entwurf, überarbeitet, fertig) als Fähnchen oben rechts auf den Vorschauboxen der Zeitlinie zeigen',
    noDescription: 'Noch keine Kurzbeschreibung – in der Seitenleiste der Szene eintragen.',
    dropHint: (title: string, timeline: string | null) => (timeline ? `„${title}“ → an der Zeitlinie „${timeline}“ ablegen` : `„${title}“ → hier ablegen (lose)`),
    placed: (kind: 'event' | 'period', title: string) => (kind === 'event' ? `„${title}“ liegt jetzt hier (als Ereignis).` : `„${title}“ liegt jetzt hier (als Zeitraum).`),
    resize: 'Höhe ändern',
    collapse: 'Erzähllinie einklappen',
    expand: 'Erzähllinie aufklappen',
    maximize: 'Erzähllinie im Vollbild (die Zeichenfläche weicht, Kapitel stehen untereinander)',
    restore: 'Vollbild beenden (Zeichenfläche wieder zeigen)',
    chapterHint: 'Klick: Kapitel auswählen · Ziehen am Hintergrund: verschieben · Doppelklick: neue Szene an dieser Stelle',
    addSceneHint: (n: number) => `Neue Szene am Ende von Kapitel ${n}`,
    created: (title: string) => `Szene „${title}“ angelegt.`,
  },
  /** Nach einem allein stehenden „@“: mit welchem Wort die gewählte Entität im Text steht */
  termPicker: {
    title: (name: string) => `Welches Wort für „${name}“?`,
    search: 'Wort wählen oder neu eingeben …',
    name: 'Name',
    useNew: (word: string) => `„${word}“ neu verwenden`,
    none: 'Tippe ein Wort, um es neu zu verwenden.',
    hint: '↑↓ wählen · Enter einsetzen · Esc schließen',
  },
  refBrowser: {
    title: (text: string) => `Verknüpfen: „${text}“`,
    insertTitle: 'Verweis einfügen',
    search: 'Person, Ort, … suchen',
    filter: 'Nach Klasse filtern',
    all: 'Alle',
    sections: { favorites: 'Favoriten · im Dokument', match: 'Passt zur Markierung', near: 'In dieser Szene', all: 'Alle Entitäten' } as Record<string, string>,
    none: 'Nichts gefunden – lege die Entität unten neu an.',
    current: 'verknüpft',
    linkedTo: (name: string) => `Diese Stelle ist mit „${name}“ verknüpft.`,
    unlink: 'Verknüpfung lösen',
    newAs: (text: string) => `„${text}“ neu anlegen als …`,
    newPlain: 'Neu anlegen als …',
    newPlainHint: (cls: string) => `Neue Entität (${cls}) anlegen und hier verweisen`,
    newHint: (text: string, cls: string) => `„${text}“ als ${cls} anlegen und verknüpfen`,
    alsoOthers: (n: number, text: string) => `Auch ${n === 1 ? 'die weitere Stelle' : `die ${n} weiteren Stellen`} „${text}“ in diesem Text verknüpfen`,
    hint: '↑↓ wählen · Enter verknüpfen · Esc schließen',
    pickFirst: 'Markiere zuerst ein Wort oder eine Textstelle – oder setze den Cursor in ein Wort.',
    oneBlock: 'Bitte nur innerhalb eines Absatzes markieren.',
    linked: (text: string, name: string) => `„${text}“ ist jetzt mit ${name} verknüpft.`,
    inserted: (name: string) => `Verweis auf ${name} eingefügt.`,
    kinds: 'Art der Verknüpfung',
    kindName: 'Ist …',
    kindSpan: 'Gehört zu …',
    kindNameHint: (key: string) => `Das Wort bzw. die Wendung ist die Entität („Clärchen“ ist Clara) – wird als Vorkommen gelernt (${key})`,
    kindSpanHint: (keys: string) =>
      `Die Stelle gehört zu Entitäten: Sie sprechen, handeln, denken hier, werden beschrieben – oder jemand erfährt hier etwas (kein Vorschlag, nichts wird gelernt)\n${keys}`,
    roleHint: (role: string, key: string | null) => (key ? `${role} (${key})` : role),
    spanTitle: (text: string) => `Gehört zu: „${text}“`,
    spanOf: (names: string) => `Diese Stelle gehört zu ${names}.`,
    unspan: 'Bezug lösen',
    role: 'Art',
    roles: { none: 'ohne', rede: 'Rede', handlung: 'Handlung', gedanke: 'Gedanke', beschreibung: 'Beschreibung', erfährt: 'Erfährt', enthüllt: 'Enthüllt' } as Record<string, string>,
    // Wissen beim Schreiben (PLAN.md §16b)
    know: 'Wissen',
    knowHints: {
      erfährt: 'Eine Figur erfährt hier etwas: Angekreuzte Personen erfahren, die übrigen Entitäten (z. B. ein Konzept) sind, was sie erfahren. Ohne Person erfährt es die Perspektivfigur.',
      enthüllt: 'Der Leser erfährt hier etwas: Kreuze an, was enthüllt wird (z. B. ein Konzept).',
    } as Record<string, string>,
    assign: (n: number) => (n === 0 ? 'Zuordnen' : `Zuordnen (${n})`),
    spanHint: '↑↓ wählen · Enter an/aus · Strg+Enter zuordnen · Esc schließen',
    spanned: (text: string, names: string) => `„${text}“ gehört jetzt zu ${names}.`,
    unspanned: 'Bezug gelöst.',
    spanLabel: (names: string) => `Gehört zu ${names} – klicken zum Ändern`,
    pin: (name: string) => `„${name}“ rechts neben dem Text anheften – zum schnellen Verknüpfen ohne diesen Browser`,
    unpin: (name: string) => `„${name}“ nicht mehr anheften`,
  },
  /** Angeheftete Entitäten rechts neben dem Text (angeheftet im Referenz-Browser) */
  refPins: {
    rail: 'Angeheftete Entitäten',
    open: (name: string) => `${name} – Markierung verknüpfen`,
    title: (text: string, name: string) => `„${text}“ mit ${name}`,
    spanWith: 'Gehört zu …',
    unpin: 'Nicht mehr anheften',
    unpinHint: (name: string) => `„${name}“ nicht mehr anheften`,
  },
  refs: {
    key: 'Verweisschlüssel',
    keyHelp:
      'Mit @schlüssel verweist du im Text auf diese Entität; beim Ändern passen sich alle Textstellen an. Darunter die Vorkommen: Wörter, mit denen sie im Text vorkommt. Sie entstehen beim Verknüpfen von selbst; wo sie nicht verknüpft sind, markiert Dispositio sie als möglichen Verweis.',
    occurrences: 'Verwendung',
    placed: 'Platziert in',
    mentioned: 'Erwähnt in',
    spanIn: { none: 'Gehört zu', rede: 'Spricht in', handlung: 'Handelt in', gedanke: 'Denkt in', beschreibung: 'Beschrieben in', erfährt: 'Erfährt in', 'erfährt:was': 'Erfahren in', enthüllt: 'Enthüllt in' } as Record<string, string>,
    none: 'Noch nirgends verwendet.',
    story: 'Geschichte',
    invalid: 'Nur Kleinbuchstaben, Ziffern und Unterstrich, mit einem Buchstaben am Anfang.',
    keys: 'Verweis (@)',
    termsLabel: 'Vorkommen im Text',
    termName: 'Der Name zählt immer als Vorkommen.',
    addTerm: 'Vorkommen hinzufügen',
    addTermShort: 'Vorkommen',
    termPlaceholder: 'z. B. Clärchen oder die Kapitänin',
    removeTerm: 'Vorkommen entfernen (Textstellen bleiben verknüpft)',
    termKnown: 'Dieses Vorkommen gibt es schon.',
    termsFull: 'Mehr Vorkommen gehen nicht – entferne erst eines.',
    autoPlaced: (names: string[]) => `${names.map((n) => `„${n}“`).join(', ')} ${names.length === 1 ? 'ist' : 'sind'} jetzt in der Szene (Verweis im Text).`,
    autoRemoved: (names: string[]) => `${names.map((n) => `„${n}“`).join(', ')} ${names.length === 1 ? 'ist' : 'sind'} nicht mehr in der Szene (kein Verweis mehr im Text).`,
  },
  terms: {
    accept: 'Verknüpfen',
    ignore: 'Hier nicht verknüpfen',
    title: (names: string[]) =>
      names.length === 1 ? `Möglicher Verweis auf „${names[0]}“ – mit ✓ verknüpfen, mit ✕ hier ignorieren` : `Möglicher Verweis auf ${names.map((n) => `„${n}“`).join(' oder ')} – mit ✓ auswählen, mit ✕ hier ignorieren`,
    acceptAll: (n: number) => (n === 1 ? 'Verweis annehmen' : `Alle ${n} eindeutigen Verweise annehmen`),
    reviewDone: 'Fertig',
  },
  // Vorschläge im Text: alles, was Dispositio erkennt, in einer Bildsprache (gepunktet unterstrichen) und einer Liste
  // Zeitraffer und Übersicht der Zeitlinien (PF 10, PLAN.md §18)
  timeView: {
    raffer: 'Zeitraffer',
    rafferHint: 'Zeitraffer ein- bzw. ausschalten: lange leere Spannen gerafft, dichte Stellen gespreizt – aus: die Zeitlinie proportional zur Zeit',
    overview: 'Übersicht über die ganze Zeit: Ausschnitt ziehen oder an eine Stelle klicken',
    spanLabel: (text: string) => `··· ${text} ···`,
    spanHint: (text: string) => `Gerafft: ${text} ohne Szene`,
    months: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
    units: {
      y: ['Jahr', 'Jahre'],
      mo: ['Monat', 'Monate'],
      w: ['Woche', 'Wochen'],
      d: ['Tag', 'Tage'],
      h: ['Stunde', 'Stunden'],
      min: ['Minute', 'Minuten'],
    } as Record<'y' | 'mo' | 'w' | 'd' | 'h' | 'min', readonly [string, string]>,
    count: {
      year: (n: number) => `Jahr ${n}`,
      day: (n: number) => `Tag ${n}`,
      hour: (n: number) => `Std. ${n}`,
      minute: (n: number) => `Min. ${n}`,
    },
  },
  // Spuren unter der Handlung (PF 12, PLAN.md §20)
  // Eigener Kalender der Geschichte (PF 16)
  calendar: {
    title: 'Kalender',
    hint: 'Gilt für alle Daten der Geschichte: Zeitlinien, Szenen, Zeitmarken im Text und ihre Erkennung. Jahr, Monat und Tag einer Angabe bleiben, wie sie sind – „der 12. des dritten Monats“ ist im neuen Kalender der 12. seines dritten Monats.',
    gregorian: 'Gregorianisch (Januar bis Dezember, Montag bis Sonntag).',
    create: 'Eigenen Kalender anlegen',
    createHint: 'Eigene Monate, Wochentage und Jahreszählung, etwa für eine erfundene Welt. Rückgängig machen geht jederzeit.',
    remove: 'Zurück zum gregorianischen Kalender',
    name: 'Name',
    months: 'Monate',
    monthName: (n: number) => `Name des ${n}. Monats`,
    monthDays: (n: number) => `Tage des ${n}. Monats`,
    days: 'Tage',
    addMonth: '+ Monat',
    weekdays: 'Wochentage',
    weekdayName: (n: number) => `Name des ${n}. Wochentags`,
    addWeekday: '+ Wochentag',
    noWeekdays: 'Keine Woche.',
    firstWeekday: 'Der 1. Tag des Jahres 1 ist ein',
    era: 'Jahreszählung',
    eraHint: 'Steht hinter der Jahreszahl, etwa „n. F.“ für „nach der Flut“; leer: keine.',
    eraPlaceholder: 'z. B. n. F.',
    leap: 'Schaltjahr',
    leapEvery: 'alle … Jahre',
    leapMonth: 'bekommt einen Tag mehr',
    up: 'Nach oben',
    down: 'Nach unten',
    removeItem: 'Entfernen',
    yearLength: (days: number, months: number) => `Ein Jahr hat ${days} Tage in ${months} ${months === 1 ? 'Monat' : 'Monaten'}.`,
    example: (date: string, weekday: string | null) => `Beispiel: ${date}${weekday ? ` ist ein ${weekday}` : ''}.`,
    outside: (n: number, sample: string) => `${n === 1 ? 'Eine Zeit gibt' : `${n} Zeiten gibt`} es in diesem Kalender nicht (z. B. „${sample}“) – sie bleiben gespeichert, bis du sie änderst.`,
    newMonth: (n: number) => `Monat ${n}`,
    newWeekday: (n: number) => `Wochentag ${n}`,
  },
  // Hintergrundereignisse (PF 15): was passiert, aber nie erzählt wird
  background: {
    kind: 'Hintergrundereignis',
  },
  tracks: {
    title: 'Spuren',
    toggleHint: 'Spuren unter der Handlung: je Figur bzw. Perspektive, wer wann wo ist, wo Stränge parallel laufen und wo sie sich treffen',
    figures: 'Figuren',
    figuresHint: 'Je Figur ihre Auftritte (Perspektive, spricht, anwesend) – aus Verweisen, Bezügen und den Entitäten der Szenen',
    pov: 'Perspektiven',
    povHint: 'Je Perspektivfigur ihre Szenen: die Erzählstränge',
    noPov: 'Ohne Perspektive',
    empty: 'Noch keine Figur tritt in einer Szene mit Zeit auf. Figuren kommen über Verweise im Text, Bezüge und die Entitäten einer Szene hinein.',
    emptyPov: 'Noch keine Szene mit Zeit.',
    missing: (n: number) => (n === 1 ? '1 Szene ohne Zeit fehlt' : `${n} Szenen ohne Zeit fehlen`),
    levels: { pov: 'Perspektive', speaks: 'spricht', present: 'anwesend', mentioned: 'erwähnt' } as Record<'pov' | 'speaks' | 'present' | 'mentioned', string>,
    stop: (who: string, scene: string, when: string, place: string, level: string) => [`${who} – ${level}`, scene, when, place].filter(Boolean).join(' · '),
    meeting: (names: string) => `Treffen: ${names}`,
    showFigure: 'In der Seitenleiste zeigen',
    close: 'Spuren ausblenden',
  },
  // Folgen sehen, bevor man loslässt (PF 55)
  consequences: {
    title: 'Danach',
    untitled: 'ohne Titel',
    issue: {
      timeMismatch: (scene: string) => `Zeitmarken in „${scene}“ passen nicht mehr zur Zeit`,
      timeOrder: (scene: string) => `„${scene}“ zählt dann ab einer anderen Szene`,
      timeWording: (scene: string) => `Wortlaut einer Zeitmarke in „${scene}“`,
      placeMismatch: (scene: string) => `Schauplatz von „${scene}“ passt nicht zum Text`,
      povMismatch: (scene: string) => `Perspektive von „${scene}“ passt nicht zum Text`,
      notAlive: (name: string, scene: string) => `${name} lebt in „${scene}“ nicht`,
      holder: (item: string, scene: string) => `${item} ist in „${scene}“ woanders`,
      placeClash: (name: string, scene: string) => `${name} ist in „${scene}“ zugleich woanders`,
      knowledgeLeak: (name: string, scene: string) => `${name} weiß in „${scene}“ etwas zu früh`,
      ageText: (name: string, scene: string) => `${name} hat in „${scene}“ das falsche Alter`,
      travel: (name: string, scene: string) => `${name} kommt in „${scene}“ zu schnell an`,
      trait: (name: string, scene: string) => `${name} sieht in „${scene}“ anders aus`,
      itemUse: (item: string, scene: string) => `${item} ist in „${scene}“ woanders`,
    },
    resolved: (what: string) => `löst sich: ${what}`,
    along: (scenes: string[]) => (scenes.length === 1 ? `${scenes[0]} wandert mit` : `${scenes.slice(0, 3).join(', ')}${scenes.length > 3 ? ' …' : ''} wandern mit`),
    after: (n: number) => (n === 1 ? 'Danach ein neuer Widerspruch' : `Danach ${n} neue Widersprüche`),
    alongDone: (n: number) => (n === 1 ? 'Eine Szene ist über ihren Anker mitgewandert.' : `${n} Szenen sind über ihren Anker mitgewandert.`),
    show: 'Zeigen',
  },
  // Text gegen Angabe von Hand (PF 56): Schauplatz bzw. Perspektive anders gesetzt, als der Draft sagt
  settingConflicts: {
    placeTitle: (text: string, setting: string) => `Der Text nennt ${text} als Ort, Schauplatz ist ${setting}. ✓ übernimmt „${text}“ als Schauplatz, ✕: ist Absicht.`,
    povTitle: (thinkers: string, pov: string) => `Der Text gibt Gedanken von ${thinkers} wieder, Perspektive ist ${pov}. ✓ übernimmt die Perspektive aus dem Text, ✕: ist Absicht.`,
    hopTitle: (thinkers: string, pov: string) => `Head-Hopping: Gedanken von ${thinkers}, erzählt wird aber aus der Sicht von ${pov}. ✕: ist Absicht.`,
    acceptPlace: (name: string) => `Schauplatz aus dem Text: „${name}“`,
    acceptPov: (name: string) => `Perspektive aus dem Text: „${name}“`,
    intended: 'Ist Absicht',
    intendedDone: 'Gemerkt: ist Absicht.',
    placeDone: (name: string) => `„${name}“ ist jetzt Schauplatz der Szene.`,
    povDone: (name: string) => `„${name}“ ist jetzt die Perspektive der Szene.`,
  },
  // Lebt die Figur hier? (AP-42): Rede, Handlung, Gedanke einer Figur, die zu der Zeit tot bzw. noch nicht geboren ist
  lifeConflicts: {
    /** `where`: „in „Szene““ bzw. „in Episode 1 („Szene“)“ */
    title: (name: string, state: 'dead' | 'unborn', where: string) =>
      `${name} ist zu der Zeit ${state === 'dead' ? `schon tot (gestorben ${where})` : `noch nicht geboren (geboren ${where})`}. ✕: ist Absicht (etwa ein Geist oder eine Vision).`,
    intended: 'Ist Absicht',
    intendedDone: 'Gemerkt: ist Absicht.',
    toDeath: 'Tod zeigen',
    toBirth: 'Geburt zeigen',
    toScene: (scene: string) => `Zur Szene „${scene}“`,
  },
  // Alter (AP-40), Merkmal und benutzter Gegenstand (AP-42) im Text: was zu der Zeit nicht stimmt
  ageConflicts: {
    /** `born`: „„Szene““ bzw. „Episode 1 („Szene“)“ */
    title: (name: string, said: number, actual: number, born: string) =>
      `Hier ist ${name} ${said} Jahre alt – nach der Geburt (${born}) aber ${actual}. ✕: ist Absicht (etwa eine Lüge oder eine Schätzung).`,
    intended: 'Ist Absicht',
    intendedDone: 'Gemerkt: ist Absicht.',
    toBirth: 'Geburt zeigen',
    toSince: 'Seit wann?',
    traitTitle: (name: string, said: string, trait: string, actual: string) => `Bei ${name} steht hier „${said}“ – zu der Zeit gilt aber ${trait}: „${actual}“. ✕: ist Absicht (etwa eine Erinnerung oder Verkleidung).`,
    itemTitle: (name: string, item: string, where: string) => `${name} benutzt hier ${item} – zu der Zeit ist es aber ${where}. ✕: ist Absicht (geliehen, heimlich genommen …).`,
  },
  // Wissenslecks (AP-43): eine Figur benutzt, was sie erst später erfährt
  knowledgeLeaks: {
    title: (name: string, about: string, role: string, scene: string) =>
      `${name} ${role === 'gedanke' ? 'denkt hier an' : 'spricht hier von'} „${about}“, erfährt es aber erst in „${scene}“. ✕: ist Absicht (eine Ahnung, jemand hat es erzählt …).`,
    intended: 'Ist Absicht',
    intendedDone: 'Gemerkt: ist Absicht.',
    toLearning: 'Wo erfahren?',
    toLearningHint: (scene: string) => `Die Stelle in „${scene}“ öffnen, an der es erfahren wird`,
  },
  // Perspektive aus dem Text (PF 50): keine gesetzt, der Text gibt nur Gedanken einer Person wieder
  // Szene teilen, wo der Text es nahelegt (PF 51)
  sceneSplit: {
    titleSeparator: 'Ein Szenentrenner mitten im Draft: Hier eine neue Szene beginnen? ✓ teilt die Szene – der Teil ab hier wird eine eigene Szene direkt dahinter. ✕: nicht teilen.',
    titleTime: 'Ein Zeitsprung am Absatzanfang: Hier eine neue Szene beginnen? ✓ teilt die Szene – der Teil ab hier wird eine eigene Szene direkt dahinter (mit Zeit und Ort aus seinem Text). ✕: nicht teilen.',
    accept: 'Hier eine neue Szene beginnen',
    ignore: 'Nicht teilen',
    ignored: 'Gemerkt: An dieser Stelle wird nicht geteilt.',
    newTitle: (title: string) => (title ? `${title} (Fortsetzung)` : 'Neue Szene'),
    done: (title: string) => `Geteilt: Der Rest steht jetzt in „${title}“.`,
    open: 'Öffnen',
  },
  povSuggest: {
    title: (name: string) => `Der Text gibt nur Gedanken von ${name} wieder. ✓ macht ${name} zur Perspektive der Szene, ✕: ohne Perspektive.`,
    titleFirstPerson: (name: string) => `Die Szene ist in der Ich-Form erzählt – wie die Szenen aus der Sicht von ${name}. ✓ macht ${name} zur Perspektive der Szene, ✕: ohne Perspektive.`,
    accept: 'Als Perspektive übernehmen',
    ignore: 'Ohne Perspektive',
    ignored: 'Gemerkt: Die Szene bleibt ohne Perspektive.',
  },
  // Zeitmarken, die nicht mehr stimmen (PF 9, PLAN.md §16d): rot an der Stelle
  timeConflicts: {
    count: (n: number) => (n === 1 ? '1 Widerspruch' : `${n} Widersprüche`),
    countHint: 'Widersprüche im Text: Zeitmarken, die nicht mehr zur Zeit der Szene bzw. zum Text passen, und Schauplatz oder Perspektive, die anders gesetzt sind, als der Text sagt – ein Klick springt zum nächsten (Alt+↓)',
    moved: (scene: string, text: string) => `Passt nicht mehr: Die Szene steht auf ${scene}, der Text sagt ${text}.`,
    order: (anchor: string, previous: string | null) =>
      previous ? `Zählt ab „${anchor}“, gelesen wird es aber nach „${previous}“.` : `Zählt ab „${anchor}“, gelesen wird es aber vor jeder Szene mit Zeit.`,
    wording: (now: string, before: string) => `Der Text sagt jetzt „${now}“, die Zeitmarke noch „${before}“.`,
    edited: (meaning: string) => `Du hast den Text dieser Zeitmarke geändert – sie bedeutet weiter „${meaning}“. Stimmt das noch?`,
    proposal: (text: string) => `Vorschlag: „${text}“.`,
    proposalTimeStays: (text: string, scene: string) => `Vorschlag: „${text}“ ab „${scene}“ – die Zeit bleibt.`,
    proposalValue: (meaning: string) => `Vorschlag: Die Zeitmarke bekommt die Zeit ${meaning}, der Text bleibt.`,
    noProposal: 'Kein passender Wortlaut gefunden – mit „Zeit“ (Strg+⇧+6) setzt du die Zeitmarke selbst.',
    acceptText: (text: string) => `Text anpassen: „${text}“`,
    weekday: (said: string, actual: string) => `Der Text nennt ${said}, das Datum fällt aber auf einen ${actual}. ✓ setzt „${actual}“, „Datum anpassen“ nimmt den nächsten ${said}, ✕: ist Absicht.`,
    acceptWeekday: (actual: string) => `Wochentag anpassen: ${actual}`,
    weekdayDate: 'Datum anpassen',
    weekdayDateHint: (text: string) => `Das Datum folgt dem Wochentag: „${text}“`,
    acceptValue: (meaning: string) => `Zeitmarke anpassen: ${meaning}`,
    fromText: 'Zeit aus dem Text',
    fromTextHint: (when: string) => `Die Szene zurück auf die Zeit, die der Text sagt (${when}); der Text bleibt`,
    countFrom: 'Ab Szene davor',
    countFromHint: (scene: string) => `Ab „${scene}“ zählen: Der Text bleibt, die Szene verschiebt sich`,
    setTime: 'Zeit …',
    setTimeHint: 'Die Zeit dieser Zeitmarke neu angeben',
    intended: 'Ist Absicht – so lassen',
    fine: 'Passt so',
    applied: (text: string) => `„${text}“ – der Text passt wieder zur Zeit.`,
    adopted: 'Die Szene folgt wieder dem Text.',
    anchored: (scene: string) => `Die Zeit zählt jetzt ab „${scene}“.`,
    valueSet: 'Die Zeitmarke sagt jetzt, was im Text steht.',
    intendedDone: 'Gemerkt: Diese Stelle ist Absicht.',
  },
  suggest: {
    count: (n: number) => (n === 1 ? '1 Vorschlag' : `${n} Vorschläge`),
    hint: 'Was Dispositio im Text erkannt hat und du noch bestätigen kannst: alle Stellen prüfen',
    kinds: {
      ref: (n: number) => (n === 1 ? '1 möglicher Verweis' : `${n} mögliche Verweise`),
      time: (n: number) => (n === 1 ? '1 Zeitangabe' : `${n} Zeitangaben`),
      place: (n: number) => (n === 1 ? '1 Ortswechsel' : `${n} Ortswechsel`),
      state: (n: number) => (n === 1 ? '1 Zustandsänderung' : `${n} Zustandsänderungen`),
      pov: (n: number) => (n === 1 ? '1 Perspektive' : `${n} Perspektiven`),
      split: (n: number) => (n === 1 ? '1 Szenenwechsel' : `${n} Szenenwechsel`),
    },
    reviewIntro: (parts: string) => `${parts}. ✓ (Alt+Enter) nimmt an, ✕ (Alt+Rücktaste) lässt die Stelle in diesem Text in Ruhe.`,
    next: 'Nächster',
    nextHint: 'Zum nächsten Vorschlag (Alt+↓, zurück mit Alt+↑)',
    done: 'Alle Vorschläge in diesem Text sind erledigt.',
  },
  // Wissensübersicht (PLAN.md §16b)
  knowPanel: {
    title: 'Wissen',
    button: 'Wissensübersicht',
    buttonHint: 'Wer weiß was seit welcher Szene – Figuren und Leser, mit dramatischer Ironie und Rätseln',
    summary: (rows: number, knowers: number) => `${rows === 1 ? '1 Information' : `${rows} Informationen`} · ${knowers === 1 ? '1 Figur' : `${knowers} Figuren`}`,
    close: 'Schließen',
    dragHint: 'Am Kopf ziehen: verschieben',
    item: 'Information',
    reader: 'Leser',
    since: (scene: string) => `seit ${scene}`,
    unknown: 'weiß es nicht',
    openElement: (name: string) => `„${name}“ öffnen`,
    legendLabel: 'Legende',
    legend: { irony: 'Leser weiß es früher', mystery: 'Figur weiß es früher', secret: 'nie enthüllt' } as Record<'irony' | 'mystery' | 'secret', string>,
    tension: {
      irony: 'Dramatische Ironie: Der Leser weiß es, bevor die Figur es erfährt.',
      mystery: 'Rätsel: Die Figur weiß es, bevor der Leser es erfährt.',
      secret: 'Figuren wissen es, dem Leser wird es nie enthüllt.',
    } as Record<'irony' | 'mystery' | 'secret', string>,
    empty: 'Noch kein Wissen erfasst. Markiere im Text eine Stelle, „Verknüpfen“ → „Gehört zu …“ → Wissen: „Erfährt“ (eine Figur erfährt etwas, z. B. ein Konzept) bzw. „Enthüllt“ (der Leser erfährt es).',
  },
  // Story Beats (PLAN.md §42): Beat-Listen am Manuskript, Struktur einer Szene, Strukturfenster
  beats: {
    title: 'Struktur',
    button: 'Struktur',
    buttonHint: 'Story Beats: wo die Wendepunkte im Buch liegen – und wo in der Weltzeit',
    close: 'Schließen',
    dragHint: 'Am Kopf ziehen: verschieben',
    summary: (beats: number, open: number) => `${beats === 1 ? '1 Beat' : `${beats} Beats`}${open > 0 ? ` · ${open} offen` : ''}`,
    sheets: 'Story Beats',
    sheetsHint:
      'Die Dramaturgie des Buchs: was an welcher Stelle der Lesereihenfolge geschehen soll. Szenen tragen die Beats. Soll ist die Stelle laut Vorlage, Ist die Stelle der ersten Szene nach Wörtern (leere Szenen zählen geschätzt).',
    none: 'Noch keine Beat-Liste. Wähle eine Vorlage – sie wird kopiert und lässt sich frei ändern.',
    template: 'Vorlage',
    add: 'Anlegen',
    addHint: 'Eine neue Beat-Liste aus dieser Vorlage',
    sheetTitle: 'Titel der Liste',
    figure: 'Bogen von',
    figureNone: 'der ganzen Geschichte',
    deleteSheet: 'Liste löschen',
    deleteSheetConfirm: (title: string) => `Die Beat-Liste „${title}“ löschen? Die Szenen bleiben, wie sie sind.`,
    addBeat: '+ Beat',
    newBeat: 'Neuer Beat',
    beatTitle: 'Titel',
    description: 'Was hier geschehen soll',
    target: 'Soll in %',
    act: 'Beginnt einen Akt',
    scenes: 'Szenen',
    addScene: '+ Szene …',
    removeScene: (label: string) => `„${label}“ lösen`,
    unplaced: 'nicht eingeordnet',
    open: 'offen',
    up: 'Nach oben',
    down: 'Nach unten',
    removeBeat: 'Beat löschen',
    removeBeatConfirm: (title: string) => `Den Beat „${title}“ löschen?`,
    targetAt: (p: string) => `Soll ${p}`,
    actual: (p: string) => `Ist ${p}`,
    show: 'Struktur zeigen',
    showHint: 'Das Strukturfenster in der Handlung öffnen',
    // Szene
    sceneSection: 'Struktur',
    sceneHint: 'Welche Beats diese Szene trägt und wie sie den Wert verschiebt, um den es geht. Daraus entstehen Struktur und Glückskurve im Strukturfenster.',
    carries: 'Trägt',
    carriesNone: 'Noch keinen Beat.',
    assign: 'Beat zuordnen …',
    noSheets: 'Noch keine Beat-Liste – anlegen in der Seitenleiste des Manuskripts.',
    shift: 'Wertverschiebung',
    shiftHint: 'Wohin die Szene den Wert verschiebt, um den es geht (z. B. Hoffnung → Verzweiflung). Ein zweiter Klick hebt die Wahl auf.',
    shiftNames: { '-2': 'stark abwärts', '-1': 'abwärts', '0': 'gleich', '1': 'aufwärts', '2': 'stark aufwärts' } as Record<string, string>,
    shiftSymbols: { '-2': '⇊', '-1': '↓', '0': '=', '1': '↑', '2': '⇈' } as Record<string, string>,
    // Binder und Film
    carriesList: (list: string) => `Trägt: ${list}`,
    // Fenster
    sheetPick: 'Beat-Liste',
    viewLabel: 'Anordnung',
    view: { reading: 'Lesereihenfolge', world: 'Weltzeit' } as Record<'reading' | 'world', string>,
    viewHint: {
      reading: 'Die Beats im Buch: Soll laut Vorlage (◇) und Ist an der ersten Szene (◆), darunter die Kapitel nach Wörtern',
      world: 'Die Beats in der erzählten Welt: die Szenen in der Reihenfolge der Weltzeit – wo die Reihenfolge eine andere ist als im Buch',
    } as Record<'reading' | 'world', string>,
    legendTarget: 'Soll',
    legendActual: 'Ist',
    curve: 'Glückskurve',
    curveHint: 'Die aufsummierte Wertverschiebung der Szenen in Lesereihenfolge',
    noCurve: 'Gib Szenen eine Wertverschiebung (Seitenleiste der Szene, Tab Manuskript), dann erscheint die Glückskurve.',
    colBeat: 'Beat',
    colTarget: 'Soll',
    colActual: 'Ist',
    colScenes: 'Szenen',
    colWorld: 'Welt',
    worldRank: (n: number) => `${n}.`,
    worldBefore: (other: string) => `spielt in der Welt vor „${other}“`,
    export: 'Exportieren',
    exportHint: 'Die Beat-Listen als Synopsis (Markdown): je Beat Soll und Ist, die Szenen mit Kurzbeschreibung, Ziel, Konflikt und Ausgang',
    fileName: (name: string) => `Struktur – ${name.replace(/[\\/:*?"<>|]+/g, ' ').trim() || 'Geschichte'}.md`,
    noScenes: 'Noch keine Szene in einem Kapitel – die Beats bekommen ihre Stelle, sobald ihre Szenen eingeordnet sind.',
    manage: 'Listen bearbeiten',
    manageHint: 'Die Beat-Listen in der Seitenleiste des Manuskripts bearbeiten',
    // Hauptliste: erscheint in Erzähllinie, Binder und Kompass
    main: 'Hauptliste',
    mainHint: 'Die erste Liste ist die Hauptliste: Ihre Beats erscheinen in der Erzähllinie, im Binder und im Kompass über dem Szenentext.',
    makeMain: 'Zur Hauptliste machen',
    // Szenen für offene Beats
    openSlotHint: (title: string, target: string | null, description: string, chapter: boolean) =>
      `„${title}“ ist noch offen${target ? ` (Soll ${target})` : ''}.${description ? `\n${description}` : ''}\nKlick: hier ${chapter ? 'ein Kapitel anlegen, dessen Szene' : 'eine Szene anlegen, die'} ihn trägt.`,
    createdLabel: (title: string) => `Beat „${title}“ angelegt`,
    created: (title: string, chapter: boolean) => (chapter ? `Kapitel „${title}“ angelegt – seine Szene trägt den Beat.` : `Szene „${title}“ angelegt – sie trägt den Beat.`),
    noteTitle: (title: string) => `Story Beat: ${title}`,
    binderTarget: (p: string, words: number) => `Soll ${p}${words > 0 ? ` ≈ ${words.toLocaleString('de-DE')} Wörter` : ''}`,
    scaffold: 'Gerüst anlegen …',
    scaffoldHint: 'Für jeden offenen Beat ein Kapitel mit einer ersten Szene (der Erklärtext des Beats als Notiz in ihrem Material) – je Akt in einem Abschnitt oder an ihrer Stelle im Buch',
    scaffoldCount: (n: number) => (n === 1 ? 'Den offenen Beat anlegen.' : `${n} offene Beats anlegen.`),
    scaffoldPerAct: 'Je Akt ein Abschnitt, je Beat ein Kapitel (sonst an ihrer Stelle im Buch: an Kapitelgrenzen ein Kapitel, mitten im Kapitel eine Szene)',
    scaffoldGo: 'Anlegen',
    scaffoldLabel: 'Gerüst aus Beats',
    scaffolded: (chapters: number, scenes: number) =>
      [chapters > 0 ? (chapters === 1 ? '1 Kapitel' : `${chapters} Kapitel`) : '', scenes > 0 ? (scenes === 1 ? '1 Szene' : `${scenes} Szenen`) : ''].filter(Boolean).join(' und ') + ' angelegt – jeder Beat hat seine Szene.',
    // eigene Vorlagen (am Konto, für alle Geschichten)
    ownTemplates: 'Eigene Vorlagen',
    saveTemplate: 'Als Vorlage speichern',
    saveTemplateHint: 'Diese Liste (ohne Szenen) als eigene Vorlage für weitere Geschichten speichern; gleicher Name ersetzt die vorhandene',
    saveTemplateName: 'Name der Vorlage',
    savedTemplate: (name: string) => `Vorlage „${name}“ gespeichert.`,
    deleteTemplate: (name: string) => `Vorlage „${name}“ löschen`,
    deleteTemplateConfirm: (name: string) => `Die eigene Vorlage „${name}“ löschen? Listen, die schon daraus entstanden sind, bleiben.`,
    // Fenster beim Anlegen einer Geschichte
    setup: {
      title: 'Story Beats wählen',
      intro: 'Womit willst du deine Geschichte gliedern? Die Beats sagen, was an welcher Stelle des Buchs geschehen soll – du kannst sie jederzeit ändern.',
      builtin: 'Vorlage',
      own: 'Eigene Vorlage laden',
      ownNone: 'Noch keine eigene Vorlage. Eine Liste lässt sich in der Seitenleiste des Manuskripts mit „Als Vorlage speichern“ für weitere Geschichten merken.',
      create: 'Eigene erstellen',
      createHint: 'Eine leere Liste – die Beats legst du in der Seitenleiste des Manuskripts an.',
      createName: 'Name der Liste',
      createDefault: 'Eigene Struktur',
      beats: (n: number) => (n === 1 ? '1 Beat' : `${n} Beats`),
      acts: (n: number) => (n === 1 ? '1 Akt' : `${n} Akte`),
      scaffold: 'Gerüst anlegen: je Akt ein Abschnitt, je Beat ein Kapitel mit einer Szene (der Erklärtext als Notiz im Material, der Anteil am Umfang als Ziel-Wortzahl)',
      length: 'Umfang des Buchs in Wörtern',
      lengthHint: 'Das Gesamtziel des Manuskripts – daraus rechnen die Beats ihre Stellen in Wörtern. Leer: ohne.',
      skip: 'Ohne Story Beats',
      apply: 'Übernehmen',
      preview: 'Verteilung der Beats im Buch',
      beforeActs: 'Vor dem ersten Akt',
      range: (from: string, to: string) => `${from} – ${to}`,
      words: (n: number) => `≈ ${n.toLocaleString('de-DE')} Wörter`,
    },
    // Kompass über dem Szenentext
    compassMore: 'Was hier geschehen soll',
    compassLess: 'Zuklappen',
    compassSheetHint: 'Die Hauptliste der Story Beats (die erste Liste am Manuskript)',
    compassAt: (p: string, here: string, length: string) => `bei ${p} · ≈ ${here} von ${length} Wörtern`,
    compassAtHint: 'Stelle im Buch nach Wörtern: alles davor und der Text dieser Szene bis jetzt (leere Szenen zählen geschätzt, das Ganze ist das Gesamtziel des Manuskripts bzw. die Schätzung)',
    compassNext: (title: string, words: string) => `als Nächstes „${title}“ in ≈ ${words} Wörtern`,
    compassNextIn: (title: string, scene: string) => `als Nächstes „${title}“ in ${scene}`,
    compassReached: (title: string) => `das Soll von „${title}“ ist erreicht`,
    compassOverdue: (list: string) => `offen davor: ${list}`,
    compassOverdueHint: 'Diese Beats haben noch keine Szene; ihre Stelle liegt vor dieser Szene',
    compassNoNext: 'kein Beat mehr danach',
    compassKind: { carried: '◆ Trägt:', next: '→ Als Nächstes:', overdue: '◇ Offen davor:' } as Record<'carried' | 'next' | 'overdue', string>,
    compassNoDescription: 'Ohne Beschreibung – in der Seitenleiste des Manuskripts ergänzen.',
    compassNothing: 'Diese Szene trägt keinen Beat, und danach kommt keiner mehr.',
  },
  matrix: {
    title: 'Auftrittsmatrix',
    button: 'Auftrittsmatrix',
    buttonHint: 'Szenen mal Entitäten: wer wann wo ist – und wer zu lange verschwindet',
    summary: (rows: number, scenes: number) => `${rows === 1 ? '1 Entität' : `${rows} Entitäten`} · ${scenes === 1 ? '1 Szene' : `${scenes} Szenen`}`,
    close: 'Schließen',
    dragHint: 'Am Kopf ziehen: verschieben',
    sequenceLabel: 'Reihenfolge der Szenen',
    sequence: { reading: 'Lesereihenfolge', world: 'Weltzeit' } as Record<'reading' | 'world', string>,
    sequenceHint: { reading: 'Die eingeordneten Szenen, wie der Leser sie liest', world: 'Alle Szenen in der Reihenfolge der erzählten Welt (soweit ihre Zeit bekannt ist)' } as Record<'reading' | 'world', string>,
    areaLabel: 'Welche Entitäten',
    all: 'Alle',
    legendLabel: 'Legende',
    level: { pov: 'Perspektive', speaks: 'spricht', present: 'anwesend', mentioned: 'erwähnt' } as Record<'pov' | 'speaks' | 'present' | 'mentioned', string>,
    gapLegend: (n: number) => `verschwunden (ab ${n} Szenen)`,
    missing: 'nicht da',
    element: 'Entität',
    onStage: 'Auftritte',
    onStageHint: 'Szenen, in denen die Entität auftritt (Perspektive, spricht oder anwesend)',
    gap: 'Lücke',
    gapHint: 'Längste Strecke ohne Auftritt zwischen zwei Auftritten (in Szenen)',
    openElement: (name: string) => `„${name}“ öffnen`,
    noScenesReading: 'Noch keine Szene in einem Kapitel – ordne Szenen ein oder wähle „Weltzeit“.',
    noScenes: 'Noch keine Szenen.',
    noRows: 'In diesen Szenen kommt noch keine solche Entität vor.',
  },
  // hell/dunkel (Knopf in der Kopfleiste)
  scheme: {
    toLight: 'Zum hellen Modus wechseln',
    toDark: 'Zum dunklen Modus wechseln',
  },
  // Vorschauboxen auf der Zeitlinie: alle oder nur Auswahl und Hover
  previews: {
    showAll: 'Alle Vorschauboxen zeigen',
    showFocus: 'Vorschauboxen nur für ausgewählte und überfahrene Szenen, die übrigen nur mit Titel',
    allHint: 'Jetzt: alle Vorschauboxen auf der Zeitlinie',
    focusHint: 'Jetzt: Boxen nur für ausgewählte (Umschalt+Klick wählt mehrere) und überfahrene Szenen, die übrigen nur mit Titel',
  },
  filter: {
    title: 'Filter',
    button: 'Filter über den Graphen',
    buttonHint: 'Entitäten wählen: passende Szenen auf Erzähllinie und Zeitlinie hervorheben, den Rest blass zeigen',
    active: (n: number) => (n === 1 ? 'Filter aktiv: 1 Entität' : `Filter aktiv: ${n} Entitäten`),
    dragHint: 'Am Kopf ziehen: verschieben',
    close: 'Schließen (der Filter bleibt)',
    modeLabel: 'Verknüpfung der Entitäten',
    mode: { and: 'Alle (UND)', or: 'Mindestens eins (ODER)' } as Record<'and' | 'or', string>,
    modeHint: { and: 'Szenen, in denen alle gewählten Entitäten vorkommen – z. B. wo sich Anna und Ben begegnen', or: 'Szenen, in denen mindestens eine der gewählten Entitäten vorkommt' } as Record<'and' | 'or', string>,
    op: { and: 'UND', or: 'ODER' } as Record<'and' | 'or', string>,
    remove: (name: string) => `„${name}“ aus dem Filter nehmen`,
    search: 'Entität suchen …',
    noMatch: 'Keine Entität gefunden.',
    none: 'Wähle eine oder mehrere Entitäten.',
    result: (n: number, total: number) => (n === 1 ? `1 von ${total} Szenen passt` : `${n} von ${total} Szenen passen`),
    scenes: (n: number) => (n === 1 ? '1 Szene' : `${n} Szenen`),
    clear: 'Filter aufheben',
  },
  overview: {
    label: (title: string) => `Übersicht „${title}“`,
    where: (label: string, chapter: string) => `Szene ${label}${chapter ? ` · ${chapter}` : ''}`,
    unassigned: 'Nicht eingeordnet',
    dragHint: 'Am Kopf ziehen: verschieben (heftet das Fenster an)',
    pin: 'Anheften: bleibt offen, lässt sich verschieben',
    unpin: 'Lösen und schließen',
    close: 'Schließen',
    when: { event: 'Zeitpunkt', period: 'Zeitraum' } as Record<'event' | 'period', string>,
    noTime: 'ohne Zeit',
    status: 'Status',
    pov: 'Perspektive',
    place: 'Schauplatz',
    words: 'Text',
    flashback: 'Rückblende',
    flashforward: 'Vorausdeutung',
    notPlaced: 'nicht auf der Fläche',
    texts: { description: 'Kurzbeschreibung', goal: 'Ziel', conflict: 'Konflikt', outcome: 'Ausgang' } as Record<'description' | 'goal' | 'conflict' | 'outcome', string>,
    elements: (n: number) => (n === 0 ? 'Entitäten' : `Entitäten (${n})`),
    noElements: 'Noch keine Entitäten – verknüpfe sie im Text oder füge sie in der Seitenleiste hinzu.',
    select: 'Auswählen',
    write: 'Schreiben',
  },
  glance: {
    label: (name: string) => `Kontext zu ${name}`,
    unknown: (key: string) => `Keine Entität mit dem Schlüssel @${key}.`,
    here: (label: string) => (label ? `In dieser Szene (${label})` : 'In dieser Szene'),
    presence: { pov: 'Perspektive', present: 'beteiligt', mentioned: 'erwähnt' } as Record<string, string>,
    notYet: 'Noch nicht in dieser Szene (der Verweis wird beim Speichern übernommen).',
    noOwn: 'Keine eigenen Angaben für diese Szene.',
    recent: 'Letzte Auftritte',
    recentAll: 'Letzte Auftritte (Lesereihenfolge)',
    firstHere: 'Erster Auftritt in dieser Szene.',
    none: 'Bisher in keiner eingeordneten Szene.',
    more: (n: number) => (n === 1 ? '1 weiterer davor' : `${n} weitere davor`),
    openScene: (title: string) => `Szenentext von „${title}“ öffnen`,
    open: 'Entität öffnen',
    hint: 'Strg+Klick aufs Wort öffnet es direkt',
    // Wissen zur Zeit der Szene (PLAN.md §16b)
    know: 'Wissen hier',
    knowWhen: { before: 'weiß es', here: 'erfährt es hier', later: 'noch nicht' } as Record<string, string>,
    reader: 'Der Leser',
    revealed: 'enthüllt',
    readerNever: 'Dem Leser wird es nie enthüllt.',
  },
  search: {
    placeholder: 'Suchen … (@ für Verweise)',
    label: 'Suche',
    none: 'Nichts gefunden.',
    kinds: { ref: 'Entität', title: 'Titel', text: 'Text', field: 'Feld', material: 'Material' } as Record<string, string>,
    inDocument: (name: string) => `Dokument „${name}“`,
  },
  issues: {
    title: 'Hinweise',
    none: 'Keine Unstimmigkeiten gefunden.',
    intro: 'Hinweise verhindern nichts. Sie zeigen, wo Text, Verbindungen und Zeitachse nicht zusammenpassen.',
    show: 'Anzeigen',
    fix: 'Jetzt korrigieren',
    info: 'Hinweis',
    warn: 'Prüfen',
    // Widersprüche an der Stelle (PF 54)
    dot: (n: number) => (n === 1 ? '1 Widerspruch' : `${n} Widersprüche`),
    nodeTitle: (n: number) => (n === 1 ? 'Ein Widerspruch' : `${n} Widersprüche`),
    nodeHint: 'Was hier nicht zusammenpasst. „Zeigen“ führt zur Stelle (im Text rot); „ist Absicht“ gibt es dort.',
    intended: 'Ist Absicht',
    intendedHint: 'So gewollt: nicht mehr als Widerspruch zeigen (gemerkt am Szenentext)',
    intendedDone: 'Gemerkt: ist Absicht.',
  },
  // Prüfbericht (PF 28, AP-45)
  report: {
    title: 'Prüfbericht',
    openWarn: (n: number) => (n === 1 ? 'offener Widerspruch' : 'offene Widersprüche'),
    counts: (info: number, checked: number) => `${info === 1 ? '1 offener Hinweis' : `${info} offene Hinweise`} · ${checked} abgehakt`,
    filter: 'Filter',
    severity: 'Art',
    severityName: { all: 'Alle', warn: 'Widersprüche', info: 'Hinweise' } as Record<'all' | 'warn' | 'info', string>,
    areas: { zeit: 'Zeit', ort: 'Ort', perspektive: 'Perspektive', zustand: 'Zustand', wissen: 'Wissen', beziehungen: 'Beziehungen', struktur: 'Struktur', verweise: 'Verweise' } as Record<IssueArea, string>,
    showChecked: 'Abgehakte zeigen',
    noneShown: 'Nichts für diesen Filter.',
    check: 'Abhaken (erledigt bzw. gesehen – die Stelle bleibt markiert; „ist Absicht“ gibt es an der Stelle)',
    uncheck: 'Wieder offen',
    unassigned: 'Nicht eingeordnet',
    entities: 'Entitäten',
    other: 'Weiteres',
    export: 'Exportieren',
    exportHint: 'Was der Filter zeigt, als Markdown-Datei (mit Kästchen für Abgehaktes)',
    exportHeading: (name: string) => `Prüfbericht: ${name}`,
    exportSummary: (date: string, warn: number, info: number, checked: number) => `Stand ${date} · ${warn} offene Widersprüche · ${info} offene Hinweise · ${checked} abgehakt`,
    exportFiltered: '*Gefiltert – es fehlen Hinweise, die der Filter ausblendet.*',
    fileName: (name: string) => `Prüfbericht – ${name.replace(/[\\/:*?"<>|]+/g, ' ').trim() || 'Geschichte'}.md`,
  },
  rail: {
    collapse: 'Leiste einklappen (nur Symbole)',
    expand: 'Leiste ausklappen',
  },
  tools: {
    label: 'Werkzeuge',
    /** Hinweis an jedem Werkzeug */
    dragHint: 'auf die Fläche ziehen oder anklicken',
    timeline: 'Zeitlinie',
    event: 'Szene',
    note: 'Notiz',
    timelineRoot: 'Zeitlinien gibt es nur in der Geschichte selbst',
    unavailable: 'Hier nicht möglich',
    createdTimeline: 'Zeitlinie angelegt',
  },
  /** Notizzettel auf der Handlungsfläche */
  notes: {
    placeholder: 'Notiz',
    text: 'Text',
    textPlaceholder: 'Was du dir merken willst …',
    color: 'Farbe',
    colorDefault: 'Farbe der Notizen',
    sizeHint: 'Verschieben: den Zettel auf der Fläche ziehen. Größe: am Griff unten rechts ziehen.',
    toEvent: 'In ein Ereignis umwandeln',
    toEventHint: 'Aus dem Zettel wird eine Szene (Ereignis) an seiner Stelle: die erste Zeile wird ihr Titel, der Rest ihre Kurzbeschreibung. Hat der Zettel eine Zeit, bekommt die Szene sie und liegt an der Zeitlinie.',
    toBackground: 'In ein Hintergrundereignis umwandeln',
    toBackgroundHint: 'Was passiert, aber nicht erzählt wird (Vorgeschichte, ein Tod, ein Krieg): eine Szene, die als „bewusst nicht erzählt“ markiert ist – mit Titel, Kurzbeschreibung und Zeit des Zettels.',
    converted: (title: string) => `„${title}“ ist jetzt ein Ereignis`,
    convertedBackground: (title: string) => `„${title}“ ist jetzt ein Hintergrundereignis`,
    untitled: 'Neues Ereignis',
    delete: 'Notiz löschen',
    // Ideen-Zettel mit Zeit und Ideenablage (PF 14)
    time: 'Zeit',
    timeHint: 'Mit Zeit hängt der Zettel an der Zeitlinie über seiner Stelle; waagerecht ziehen ändert die Zeit.',
    undated: 'Ohne Zeit – der Zettel liegt in der Ideenablage. Auf eine Zeitlinie ziehen oder hier eine Zeit angeben.',
    clearTime: 'Zeit entfernen (zurück in die Ideenablage)',
    pinTo: (timeline: string) => `An „${timeline}“ heften – mit der Zeit an dieser Stelle`,
    noReckoning: 'Diese Zeitlinie hat keine Zeitrechnung – der Zettel bleibt ohne Zeit.',
    ideas: 'Ideen',
    ideasHint: 'Zettel ohne Zeit: anklicken zeigt ihn, auf eine Zeitlinie ziehen gibt ihm die Zeit an der Stelle',
    ideasEmpty: 'Keine Zettel ohne Zeit.',
  },
  add: {
    open: 'Hinzufügen',
    title: 'Hinzufügen',
    classes: 'Klasse',
    existing: 'Vorhanden',
    existingHint: {
      normal: 'Wähle einen vorhandenen Eintrag, der kopiert wird.',
      none: 'Zeitlinien legst du hier neu an.',
      loose: 'Wähle eine vorhandene Entität, die als Kopie (samt Texten) hierher kommt.',
    },
    none: 'Noch nichts vorhanden.',
    accept: 'Hinzufügen',
    params: (name: string) => `${name} anlegen`,
    create: 'Anlegen',
    refKey: 'Verweisschlüssel (optional)',
    timeline: 'Zeitlinie',
    emptyHere: 'Hier lässt sich nichts anlegen.',
    newClass: 'Neue Klasse anlegen',
    newClassTitle: 'Neue Klasse',
    newElement: (name: string) => `Neu: ${name}`,
  },
  errors: {
    generic: 'Etwas ist schiefgelaufen.',
    area: (name: string) => `${name}: Hier ist etwas schiefgelaufen. Der Rest der Geschichte lässt sich weiter bearbeiten.`,
    panels: 'Fenster',
    sidebar: 'Seitenleiste',
    network: 'Keine Verbindung zum Server.',
    notFound: 'Nicht gefunden.',
  },
};
